import axios from 'axios';
import { useAppStore } from './store';
import { resolveBackendBase, usesSameOriginApiProxy } from './lib/backendBase';
import { isPlaceholderCatalogSubtype } from './lib/vitrineCatalogFilter';
import type { CartListResponse, ProductGroup, ProductGroupMediaSlide } from './types';

const BACKEND = resolveBackendBase();
const api = axios.create({ baseURL: `${BACKEND}/api`, timeout: 90_000 });

/** host.tld/path без схемы — Telegram WebView не открывает такие src. */
function withHttpsSchemeIfBareHost(u: string): string {
  if (!u || /^https?:\/\//i.test(u) || u.startsWith('//') || u.startsWith('/')) return u;
  if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(\/|$)/i.test(u)) return `https://${u}`;
  return u;
}

/** Turn API-relative media paths into absolute URLs for <img src>. */
export function resolveBackendMediaUrl(url: string | undefined | null): string {
  let u = withHttpsSchemeIfBareHost(String(url ?? '').trim());
  while (u.startsWith('/https://') || u.startsWith('/http://')) {
    u = u.slice(1);
  }
  if (u.includes('/api/media/catalog/') || u.includes('/api/media/motor-handbook/')) {
    if (/^https?:\/\//i.test(u)) return u;
    const base = BACKEND.replace(/\/$/, '');
    const host = base || (typeof window !== 'undefined' ? window.location.origin : '');
    const withScheme = host && !/^https?:\/\//i.test(host) ? `https://${host.replace(/^\/+/, '')}` : host;
    const qIdx = u.indexOf('?');
    const pathOnly = qIdx === -1 ? u : u.slice(0, qIdx);
    const query = qIdx === -1 ? '' : u.slice(qIdx);
    const path = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
    return withScheme ? `${withScheme}${path}${query}` : u;
  }
  if (!u) return '';
  /** Mini App на https: iOS/Telegram блокируют http-картинки (mixed content), даже если хост «правильный». */
  if (
    typeof window !== 'undefined' &&
    window.location?.protocol === 'https:' &&
    /^http:\/\//i.test(u)
  ) {
    try {
      const tu = new URL(u);
      if (/\/uploads\//i.test(tu.pathname)) {
        tu.protocol = 'https:';
        u = tu.toString();
      }
    } catch {
      /* ignore */
    }
  }
  const base = BACKEND.replace(/\/$/, '');

  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      const host = (parsed.hostname || '').toLowerCase();
      let path = parsed.pathname || '';
      if (path.startsWith('/uploads/catalog-systems/')) {
        path = path.replace('/uploads/catalog-systems/', '/uploads/tenants/default/catalog-systems/');
      }
      const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
      if (base && (isLocalHost || /\/uploads\//i.test(path))) {
        const q = parsed.search || '';
        if (path.startsWith('/uploads/')) {
          return `${base}${path}${q}`;
        }
      }
    } catch {
      /* ignore */
    }
    if (base) {
      const idx = u.search(/\/uploads\//i);
      if (idx !== -1) {
        let pathAndQuery = u.slice(idx);
        if (pathAndQuery.includes('/uploads/catalog-systems/')) {
          pathAndQuery = pathAndQuery.replace(
            '/uploads/catalog-systems/',
            '/uploads/tenants/default/catalog-systems/',
          );
        }
        const hashPos = pathAndQuery.indexOf('#');
        if (hashPos !== -1) pathAndQuery = pathAndQuery.slice(0, hashPos);
        return `${base}${pathAndQuery}`;
      }
    }
    return u;
  }
  if (u.startsWith('//')) return `https:${u}`;
  let path = u.startsWith('/') ? u : `/${u}`;
  if (path.startsWith('/uploads/catalog-systems/')) {
    path = path.replace('/uploads/catalog-systems/', '/uploads/tenants/default/catalog-systems/');
  }
  return BACKEND ? `${BACKEND}${path}` : path;
}

/** Mini App (Cloudflare Pages) — relative `/uploads/...` must target the API host. */
function mapProductGroupsResolveImageUrl<T>(data: T): T {
  if (!Array.isArray(data)) return data;
  return (data as unknown[]).map((row) => {
    if (!row || typeof row !== 'object') return row;
    const o = row as Record<string, unknown>;
    const raw = o.image_url;
    if (raw == null || raw === '') return row;
    const resolved = resolveBackendMediaUrl(String(raw));
    return resolved ? { ...o, image_url: resolved } : row;
  }) as T;
}

function resolvePayloadTopLevelImageUrl<T>(payload: T): T {
  if (!payload || typeof payload !== 'object') return payload;
  const p = payload as Record<string, unknown>;
  const raw = p.image_url;
  if (raw == null || raw === '') return payload;
  const resolved = resolveBackendMediaUrl(String(raw));
  return resolved ? ({ ...p, image_url: resolved } as T) : payload;
}

const SYNTHETIC_MEDIA_ID_BASE = 900_000_000;

export function parseV2SystemMedia(media: unknown): ProductGroupMediaSlide[] {
  if (!Array.isArray(media)) return [];
  return [...media]
    .map((raw: Record<string, unknown>, i: number) => {
      let id = Number(raw?.id);
      if (!Number.isFinite(id) || id <= 0) {
        id = SYNTHETIC_MEDIA_ID_BASE + i;
      }
      const url = resolveBackendMediaUrl(String(raw?.url ?? ''));
      const t = String(raw?.type ?? 'image').toLowerCase();
      const type: 'image' | 'video' = t === 'video' ? 'video' : 'image';
      const so = raw?.sort_order;
      const sort_order = so != null && so !== '' ? Number(so) : undefined;
      const posterRaw = raw?.poster_url != null ? String(raw.poster_url).trim() : '';
      let poster_url: string | undefined;
      if (type === 'video') {
        if (posterRaw) {
          poster_url = resolveBackendMediaUrl(posterRaw) || undefined;
        } else if (id > 0 && id < SYNTHETIC_MEDIA_ID_BASE) {
          const base = BACKEND.replace(/\/$/, '');
          poster_url = base ? `${base}/api/media/catalog/${id}/poster` : `/api/media/catalog/${id}/poster`;
        }
      }
      return {
        id,
        url,
        type,
        poster_url,
        sort_order: Number.isFinite(sort_order) ? sort_order : undefined,
        is_primary: Boolean(raw?.is_primary),
      };
    })
    .filter((x) => Number.isFinite(x.id) && x.id > 0 && x.url)
    .sort((a, b) => (a.sort_order ?? a.id) - (b.sort_order ?? b.id) || a.id - b.id);
}

function coverUrlFromV2Gallery(gallery: ProductGroupMediaSlide[]): string | undefined {
  const imgs = gallery.filter((g) => g.type === 'image');
  if (!imgs.length) return undefined;
  const primary = imgs.find((i) => i.is_primary);
  return (primary || imgs[0])?.url;
}

/** Simple slug generator for legacy data */
function toSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-zа-я0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);
}

/** GET `/products/grouped/v2` (Branch→Subtype→Products) → `ProductGroup`. */
function catalogBranchRowToProductGroup(row: unknown): ProductGroup | null {
  if (!row || typeof row !== 'object') return null;
  const s = row as Record<string, unknown>;
  
  // Support both V2 (subtype_slug) and legacy formats
  let subtypeSlug = String(s.subtype_slug ?? s.subcategory_slug ?? '').trim();
  let branchSlug  = String(s.branch_slug ?? s.category_slug ?? '').trim();
  
  // For legacy data: generate slugs from name/category
  if (!subtypeSlug && s.name) {
    subtypeSlug = toSlug(String(s.name));
  }
  if (!branchSlug && s.category) {
    branchSlug = toSlug(String(s.category));
  }
  
  if (!subtypeSlug) return null;

  const rawImg = s.image_url;
  const imageResolved = rawImg != null && rawImg !== '' ? resolveBackendMediaUrl(String(rawImg)) : undefined;

  const gallery = parseV2SystemMedia(s.gallery);
  const coverUrl = coverUrlFromV2Gallery(gallery) || imageResolved;

  const rawVariants: unknown[] = Array.isArray(s.variants) ? s.variants : [];
  const variants = rawVariants.map((v: unknown) => {
    const vv = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    const voltageRaw = vv.voltage_type != null ? String(vv.voltage_type).trim() : '';
    const batteryFromVoltage = voltageRaw === 'АКБ' || voltageRaw === 'АКБ ';
    return {
      id:                  Number(vv.catalog_variant_id ?? vv.id ?? 0),
      catalog_variant_id:  vv.catalog_variant_id != null ? Number(vv.catalog_variant_id) : undefined,
      motor_model:         vv.motor_model   != null ? String(vv.motor_model)   : undefined,
      motor_full_name:     vv.motor_full_name != null ? String(vv.motor_full_name) : undefined,
      protocol:            vv.protocol      != null ? String(vv.protocol)      : undefined,
      interface_type:      vv.interface_type != null ? String(vv.interface_type) : undefined,
      control_types:       Array.isArray(vv.control_types) ? (vv.control_types as string[]) : undefined,
      variant_specs:       vv.variant_specs && typeof vv.variant_specs === 'object'
        ? (vv.variant_specs as Record<string, unknown>) : undefined,
      tier_slug:           vv.tier_slug != null ? String(vv.tier_slug) : undefined,
      is_battery_powered:  vv.is_battery_powered != null
        ? Boolean(vv.is_battery_powered)
        : batteryFromVoltage,
      color:               vv.color         != null ? String(vv.color)         : undefined,
      power_watts:         vv.power_watts   != null ? Number(vv.power_watts)   : undefined,
      price_matrix:        Array.isArray(vv.price_matrix) && vv.price_matrix.length > 0
        ? (vv.price_matrix as import('./types').PricePoint[])
        : (Array.isArray(vv.dimensions_price_matrix) && vv.dimensions_price_matrix.length > 0
          ? (vv.dimensions_price_matrix as import('./types').PricePoint[])
          : undefined),
    };
  });

  const schema = s.selector_schema && typeof s.selector_schema === 'object' && !Array.isArray(s.selector_schema)
    ? (s.selector_schema as ProductGroup['selector_schema'])
    : {};

  const backendSystemSlug =
    typeof s.catalog_v2_slug === 'string' && String(s.catalog_v2_slug).trim() !== ''
      ? String(s.catalog_v2_slug).trim()
      : null;

  return {
    group_id:        `st::${branchSlug}::${subtypeSlug}`,
    catalog_v2_slug: backendSystemSlug || subtypeSlug,
    name:            String(s.name ?? subtypeSlug),
    category:        String(s.category ?? branchSlug),
    subcategory:     String(s.subcategory ?? s.name ?? ''),
    image_url:       coverUrl,
    gallery:         gallery.length ? gallery : undefined,
    is_premium:      false,
    protocols:       Array.isArray(s.protocols) ? (s.protocols as unknown[]).map(String) : [],
    interface_types: Array.isArray(s.interface_types) ? (s.interface_types as unknown[]).map(String) : [],
    has_battery:     Boolean(s.has_battery),
    has_wired:       Boolean(s.has_wired),
    min_price:       s.min_price != null ? Number(s.min_price) : undefined,
    short_summary:   s.short_summary  != null ? String(s.short_summary)  : undefined,
    short_description: s.short_description != null ? String(s.short_description) : undefined,
    selector_schema: schema,
    variants,
    variants_count:  variants.length,
    subtype_id:        s.subtype_id        != null ? Number(s.subtype_id)        : undefined,
    branch_id:         s.branch_id         != null ? Number(s.branch_id)         : undefined,
    branch_sort_order: s.branch_sort_order != null ? Number(s.branch_sort_order) : undefined,
  };
}


api.interceptors.request.use((config) => {
  const token = useAppStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default api;

export type PublicBuildMeta = {
  git_sha_short: string | null;
  deploy_id_short: string | null;
  server_time_utc: string;
  process_boot_utc: string;
};

/** Публичный `/api/build-meta` — время процесса API и git/deploy (для профиля / dev). */
export const getPublicBuildMeta = (opts?: { bustCache?: boolean }) =>
  api
    .get<PublicBuildMeta>('/build-meta', {
      params: opts?.bustCache ? { _t: Date.now() } : undefined,
    })
    .then((r) => r.data);

// Auth
export const authTelegram = (initData: string) =>
  api.post('/auth/telegram', { init_data: initData }).then((r) => r.data);

// Products
export const getCategories = () =>
  api.get('/products/categories').then((r) => r.data.categories as string[]);

/** Порядок/видимость табов категорий на витрине (ключ = нормализованный label после alias). */
export type VitrineCategoryConfigItem = {
  category_key: string;
  sort_order: number;
  is_visible: boolean;
  display_label: string | null;
};

export const getVitrineCategoryConfig = (): Promise<VitrineCategoryConfigItem[]> =>
  api
    .get<{ items: VitrineCategoryConfigItem[] }>('/products/vitrine-category-config')
    .then((r) => (Array.isArray(r.data?.items) ? r.data.items : []));

export type AdminVitrineCategoryRow = VitrineCategoryConfigItem & { id?: number };

/** Витрина, встроенная в GET /admin/products?include_vitrine=true (один RTT). */
export type AdminVitrineBundle = {
  items: AdminVitrineCategoryRow[];
  discovered_keys: string[];
};

/** Legacy: массив продуктов; bundled: { products, vitrine }. */
export function parseAdminProductsResponse(data: unknown): {
  products: unknown[];
  vitrineEmbedded: AdminVitrineBundle | null;
} {
  if (Array.isArray(data)) {
    return { products: data, vitrineEmbedded: null };
  }
  if (data && typeof data === 'object') {
    const o = data as {
      products?: unknown;
      vitrine?: { items?: unknown; discovered_keys?: unknown };
    };
    if (Array.isArray(o.products) && o.vitrine && typeof o.vitrine === 'object' && Array.isArray(o.vitrine.items)) {
      const items = o.vitrine.items as AdminVitrineCategoryRow[];
      const dk = Array.isArray(o.vitrine.discovered_keys)
        ? (o.vitrine.discovered_keys as unknown[]).map((x) => String(x))
        : [];
      return { products: o.products, vitrineEmbedded: { items, discovered_keys: dk } };
    }
    if (Array.isArray(o.products)) {
      return { products: o.products, vitrineEmbedded: null };
    }
  }
  return { products: [], vitrineEmbedded: null };
}

/** Сообщение об ошибке Axios для админских запросов (каталог, витрина). */
const BAKED_BACKEND_URL = String(import.meta.env.VITE_BACKEND_URL ?? '').trim();
const RAILWAY_BACKEND_FALLBACK = 'https://backend-production-8f11.up.railway.app';

function detailStringFromFastApi(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const d = (data as { detail?: unknown }).detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const parts = d
      .map((x) =>
        x && typeof x === 'object' && 'msg' in x ? String((x as { msg: unknown }).msg) : '',
      )
      .filter(Boolean);
    return parts.length ? parts.join('; ') : null;
  }
  return null;
}

/** Повтор при обрыве сети / таймауте (Telegram WebView + медленный cold start Railway). */
export async function withAdminApiRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let last: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      last = e;
      if (!axios.isAxiosError(e) || e.response) throw e;
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, 2000 * (i + 1)));
        continue;
      }
    }
  }
  throw last;
}

export function formatAxiosAdminError(e: unknown, fallback: string): string {
  if (!axios.isAxiosError(e)) return fallback;
  if (!e.response) {
    if (!BAKED_BACKEND_URL) {
      return 'VITE_BACKEND_URL не задан — сборка без Railway URL. Установите его в Cloudflare Pages → Settings → Environment Variables → повторите деплой → полностью закройте мини-апп в Telegram и откройте снова.';
    }
    const origin = (
      typeof window !== 'undefined' && usesSameOriginApiProxy()
        ? `${window.location.origin} → ${RAILWAY_BACKEND_FALLBACK}`
        : BAKED_BACKEND_URL || RAILWAY_BACKEND_FALLBACK
    ).replace(/\/$/, '');
    const timedOut =
      e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
    if (timedOut) {
      return `Сервер не успел ответить вовремя (${origin}). Подождите 30 сек и нажмите «Сохранить» снова. Railway: backend и Postgres — Running.`;
    }
    return `Нет ответа от сервера (${origin}). Проверьте: 1) интернет; 2) Railway backend — Deployments → Active; 3) Postgres не Crashed. Закройте мини-апп полностью и откройте снова.`;
  }
  const st = e.response.status;
  const detail = detailStringFromFastApi(e.response.data);
  if (st === 401)
    return 'Сессия истекла или токен ещё не загружен. Закройте мини-апп полностью и откройте снова из бота.';
  if (st === 403)
    return 'Нет прав доступа (403). Убедитесь что ваша роль — admin. Если роль менялась — закройте мини-апп и откройте снова.';
  if (st === 500)
    return `Ошибка сервера (500). Вероятная причина: не применены миграции БД. Откройте Railway → Deployments → Logs и убедитесь что строка "alembic upgrade head" завершилась успешно. ${detail ? `Детали: ${detail}` : ''}`.trim();
  if (st === 503 && detail) return detail;
  if (detail) return detail;
  if (st) return `Ошибка ${st}`;
  return fallback;
}

export const getAdminVitrineCategories = () =>
  api
    .get<{ items: AdminVitrineCategoryRow[]; discovered_keys: string[] }>(
      '/admin/catalog/vitrine-categories'
    )
    .then((r) => r.data);

export const putAdminVitrineCategories = (items: VitrineCategoryConfigItem[]) =>
  api
    .put<{ items: AdminVitrineCategoryRow[] }>('/admin/catalog/vitrine-categories', { items })
    .then((r) => r.data);

/** Админ: вкладка «Пульты и аксессуары» — строки `accessories`. */
export type AdminAccessoryRow = {
  id: number;
  name: string;
  price: number | null;
  image_url?: string | null;
  description?: string | null;
  is_active: boolean;
  category?: string | null;
  sku?: string | null;
  sort_order: number;
  vitrine_group_id?: number | null;
  variant_label?: string | null;
};

export type AdminAccessoryPatch = Partial<{
  name: string;
  price: number | null;
  image_url: string | null;
  description: string | null;
  is_active: boolean;
  category: string | null;
  sku: string | null;
  sort_order: number;
  vitrine_group_id: number | null;
  variant_label: string | null;
}>;

export type AdminAccessoryVitrineGroupRow = {
  id: number;
  slug: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  hero_image_url?: string | null;
  layout: 'tile' | 'row';
  section: string;
  sort_order: number;
  is_active: boolean;
  variants: Array<{
    id: number;
    name: string;
    variant_label?: string | null;
    price: number | null;
    image_url?: string | null;
    description?: string | null;
    sku?: string | null;
    is_active?: boolean;
    sort_order?: number;
  }>;
};

export type AccessoryVitrineVariant = {
  accessory_id: number;
  label: string;
  name: string;
  price: number | null;
  image_url?: string | null;
  description?: string | null;
};

export type AccessoryVitrineTile = {
  id: number;
  slug: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  hero_image_url?: string | null;
  min_price?: number | null;
  sort_order?: number;
  variants: AccessoryVitrineVariant[];
};

export type AccessoryVitrineRow = {
  id: number;
  name: string;
  price: number | null;
  image_url?: string | null;
  description?: string | null;
  category?: string | null;
  sort_order?: number;
};

export type AccessoryVitrineSection = {
  label: string;
  tiles: AccessoryVitrineTile[];
  rows: AccessoryVitrineRow[];
};

export const getAdminAccessories = (): Promise<AdminAccessoryRow[]> =>
  api.get<AdminAccessoryRow[]>('/admin/accessories').then((r) => r.data);

export const patchAdminAccessory = (id: number, body: AdminAccessoryPatch): Promise<AdminAccessoryRow> =>
  api.patch<AdminAccessoryRow>(`/admin/accessories/${id}`, body).then((r) => r.data);

export type AdminAccessoryCreateBody = {
  name: string;
  price?: number | null;
  category?: string | null;
  sku?: string | null;
  sort_order?: number;
  is_active?: boolean;
  vitrine_group_id?: number | null;
  variant_label?: string | null;
  description?: string | null;
  image_url?: string | null;
};

export const postAdminAccessory = (body: AdminAccessoryCreateBody): Promise<AdminAccessoryRow> =>
  api.post<AdminAccessoryRow>('/admin/accessories', body).then((r) => r.data);

export const deleteAdminAccessory = (id: number): Promise<{ ok: boolean }> =>
  api.delete<{ ok: boolean }>(`/admin/accessories/${id}`).then((r) => r.data);

export const getAccessoryVitrine = () =>
  api.get<{ sections: AccessoryVitrineSection[]; section_order?: string[] }>('/products/accessories/vitrine').then((r) => r.data);

export const getAdminAccessoryVitrineGroups = (): Promise<AdminAccessoryVitrineGroupRow[]> =>
  api.get<AdminAccessoryVitrineGroupRow[]>('/admin/accessory-vitrine-groups').then((r) => r.data);

export const patchAdminAccessoryVitrineGroup = (
  id: number,
  body: Partial<AdminAccessoryVitrineGroupRow>,
): Promise<AdminAccessoryVitrineGroupRow> =>
  api.patch<AdminAccessoryVitrineGroupRow>(`/admin/accessory-vitrine-groups/${id}`, body).then((r) => r.data);

export const postAdminAccessoryVitrineGroup = (body: {
  slug: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  hero_image_url?: string | null;
  layout?: 'tile' | 'row';
  section?: string;
  sort_order?: number;
  is_active?: boolean;
}): Promise<AdminAccessoryVitrineGroupRow> =>
  api.post<AdminAccessoryVitrineGroupRow>('/admin/accessory-vitrine-groups', body).then((r) => r.data);

export const deleteAdminAccessoryVitrineGroup = (id: number): Promise<{ ok: boolean }> =>
  api.delete<{ ok: boolean }>(`/admin/accessory-vitrine-groups/${id}`).then((r) => r.data);

export const reorderAdminAccessoryVitrineGroups = (ids: number[]): Promise<{ ok: boolean; reordered: number }> =>
  api.post<{ ok: boolean; reordered: number }>('/admin/accessory-vitrine-groups/reorder', { ids }).then((r) => r.data);

/** Админ: иерархия каталога L1→L2 (ветки и подтипы в БД). */
export type VitrineBranchRow = {
  id: number;
  slug: string;
  title: string;
  display_label: string;
  sort_order: number;
  show_on_vitrine: boolean;
  is_active: boolean;
  icon_url?: string | null;
  subtype_count: number;
  systems_count: number;
  priced_variant_count: number;
  has_content: boolean;
};

export const getVitrineBranches = async (): Promise<VitrineBranchRow[]> => {
  try {
    const r = await api.get('/products/vitrine');
    const branches = r.data?.branches;
    if (Array.isArray(branches) && branches.length > 0) {
      return branches as VitrineBranchRow[];
    }
  } catch (e) {
    console.warn('[getVitrineBranches] /products/vitrine failed, trying catalog-tree', e);
  }
  try {
    const tree = await getCatalogBranchOrder();
    return tree.map((b) => ({
      id: b.id,
      slug: b.slug,
      title: b.title,
      display_label: b.title,
      sort_order: b.sort_order,
      show_on_vitrine: true,
      is_active: true,
      subtype_count: 0,
      systems_count: 0,
      priced_variant_count: 0,
      has_content: true,
    }));
  } catch {
    return [];
  }
};

export const putAdminVitrineBranches = (
  items: Array<{
    id: number;
    sort_order: number;
    show_on_vitrine: boolean;
    vitrine_label?: string | null;
    icon_url?: string | null;
  }>,
): Promise<{ ok: boolean; updated: number }> =>
  api.put('/admin/catalog/branches/vitrine', { items }).then((r) => r.data);

export type AdminCatalogBranchRow = {
  id: number;
  slug: string;
  title: string;
  sort_order: number;
  is_active: boolean;
  subtype_count: number;
  show_on_vitrine?: boolean;
  vitrine_label?: string | null;
  icon_url?: string | null;
  has_content?: boolean;
  health?: string;
};

export type AdminCatalogSubtypeRow = {
  id: number;
  branch_id: number;
  slug: string;
  title: string;
  sort_order: number;
  is_active: boolean;
  catalog_system_id: number | null;
  option_schema: Record<string, unknown>;
  sku_count: number;
};

export const getAdminCatalogBranches = (): Promise<AdminCatalogBranchRow[]> =>
  api.get<AdminCatalogBranchRow[]>('/admin/catalog/branches').then((r) => r.data);

export const getAdminCatalogSubtypes = (branchId: number): Promise<AdminCatalogSubtypeRow[]> =>
  api.get<AdminCatalogSubtypeRow[]>(`/admin/catalog/branches/${branchId}/subtypes`).then((r) => r.data);

export const patchAdminCatalogSubtype = (
  subtypeId: number,
  body: Partial<{
    title: string;
    sort_order: number;
    is_active: boolean;
    option_schema: Record<string, unknown>;
    catalog_system_id: number | null;
  }>,
): Promise<{ ok: boolean; id: number }> =>
  api.patch(`/admin/catalog/subtypes/${subtypeId}`, body).then((r) => r.data);

export type AdminSubtypeSkusResponse = {
  total: number;
  items: Array<{
    id: number;
    external_id?: string | null;
    category: string;
    subcategory?: string | null;
    name: string;
    protocol?: string | null;
    color?: string | null;
    is_active: boolean;
    subtype_id?: number | null;
  }>;
};

export const getAdminSubtypeSkus = (
  subtypeId: number,
  params?: { skip?: number; limit?: number },
): Promise<AdminSubtypeSkusResponse> =>
  api
    .get<AdminSubtypeSkusResponse>(`/admin/catalog/subtypes/${subtypeId}/skus`, { params })
    .then((r) => r.data);

export const postAdminSubtypeBatchSkus = (
  subtypeId: number,
  body: { set: Record<string, unknown>; regenerate_external_id?: boolean },
): Promise<{ ok: boolean; updated: number }> =>
  api.post(`/admin/catalog/subtypes/${subtypeId}/batch-skus`, body).then((r) => r.data);

export const createAdminCatalogBranch = (body: {
  title: string;
  slug?: string;
  sort_order?: number;
  is_active?: boolean;
}): Promise<AdminCatalogBranchRow> =>
  api.post<AdminCatalogBranchRow>('/admin/catalog/branches', body).then((r) => r.data);

export const patchAdminCatalogBranch = (
  branchId: number,
  body: Partial<{
    title: string;
    slug: string;
    sort_order: number;
    is_active: boolean;
    show_on_vitrine: boolean;
    vitrine_label: string | null;
    icon_url: string | null;
  }>,
): Promise<AdminCatalogBranchRow> =>
  api.patch<AdminCatalogBranchRow>(`/admin/catalog/branches/${branchId}`, body).then((r) => r.data);

export const deleteAdminCatalogBranch = (branchId: number): Promise<{ ok: boolean; deleted_id: number }> =>
  api.delete(`/admin/catalog/branches/${branchId}`).then((r) => r.data);

export const createAdminCatalogSubtype = (
  branchId: number,
  body: {
    title: string;
    slug?: string;
    sort_order?: number;
    is_active?: boolean;
    catalog_system_id?: number | null;
    option_schema?: Record<string, unknown>;
  },
): Promise<AdminCatalogSubtypeRow> =>
  api.post<AdminCatalogSubtypeRow>(`/admin/catalog/branches/${branchId}/subtypes`, body).then((r) => r.data);

export const deleteAdminCatalogSubtype = (
  subtypeId: number,
  force = false,
): Promise<{ ok: boolean; deleted_id: number; unassigned_skus: number }> =>
  api.delete(`/admin/catalog/subtypes/${subtypeId}?force=${force}`).then((r) => r.data);

export const initBranchesSortOrder = (): Promise<{ ok: boolean; updated: number }> =>
  api.post('/admin/catalog/branches/init-sort-order').then((r) => r.data);

export const reorderAdminBranches = (ids: number[]): Promise<{ ok: boolean }> =>
  api.post('/admin/catalog/branches/reorder', { ids }).then((r) => r.data);

export const reorderAdminSubtypes = (branchId: number, ids: number[]): Promise<{ ok: boolean }> =>
  api.post(`/admin/catalog/branches/${branchId}/subtypes/reorder`, { ids }).then((r) => r.data);

export const reorderAdminCatalogSystems = (subtypeId: number, ids: number[]): Promise<{ ok: boolean }> =>
  api.post(`/admin/catalog/subtypes/${subtypeId}/systems/reorder`, { ids }).then((r) => r.data);

export const uploadSubtypeImage = (subtypeId: number, file: File): Promise<{ ok: boolean; image_url: string }> => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/admin/catalog/subtypes/${subtypeId}/upload-image`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);
};

export const assignProductSubtype = (
  productId: number,
  subtypeId: number | null,
): Promise<{ ok: boolean; product_id: number; subtype_id: number | null }> =>
  api.patch(`/admin/catalog/products/${productId}/subtype`, { subtype_id: subtypeId }).then((r) => r.data);

export const bulkAssignSubtype = (
  productIds: number[],
  subtypeId: number | null,
): Promise<{ ok: boolean; updated: number }> =>
  api.post('/admin/catalog/products/bulk-assign-subtype', { product_ids: productIds, subtype_id: subtypeId }).then((r) => r.data);

export const bulkToggleActive = (
  productIds: number[],
  isActive: boolean,
): Promise<{ ok: boolean; updated: number }> =>
  api.post('/admin/catalog/products/bulk-toggle-active', { product_ids: productIds, is_active: isActive }).then((r) => r.data);

export const backfillCatalogSubtypes = (): Promise<{ ok: boolean; processed: number; updated_subtype_id: number }> =>
  api.post('/admin/catalog/backfill-subtypes').then((r) => r.data);

export const flushCatalogCache = (): Promise<{ ok: boolean; flushed: string[] }> =>
  api.post('/admin/catalog/flush-cache').then((r) => r.data);

export const bulkAssignByCategory = (
  category: string,
  subcategory: string | null,
  subtypeId: number,
): Promise<{ ok: boolean; updated: number }> =>
  api.post('/admin/catalog/products/assign-by-category', { category, subcategory: subcategory || null, subtype_id: subtypeId }).then((r) => r.data);

export const uploadCatalogSystemImage = (systemId: number, file: File): Promise<{ ok: boolean; id: number; image_url: string }> => {
  const fd = new FormData();
  fd.append('image', file);
  return api.post<{ ok: boolean; id: number; image_url: string }>(`/admin/catalog-systems/${systemId}/image`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);
};

/** Админ: вкладка «Услуги». */
export type AdminServiceShopRow = {
  id: number;
  name: string;
  price: string | null;
  is_active: boolean;
};

export const getAdminServicesShop = (): Promise<AdminServiceShopRow[]> =>
  api.get<AdminServiceShopRow[]>('/admin/services-shop').then((r) => r.data);

export const patchAdminServiceShop = (
  id: number,
  body: Partial<{ name: string; price: string | null; is_active: boolean }>,
): Promise<AdminServiceShopRow> =>
  api.patch<AdminServiceShopRow>(`/admin/services-shop/${id}`, body).then((r) => r.data);

export const getProducts = (params?: { category?: string; search?: string; limit?: number }) =>
  api.get('/products/', { params }).then((r) => r.data);

export const getGroupedProducts = (params?: {
  category?: string;
  search?: string;
}): Promise<ProductGroup[]> =>
  api
    .get('/v3/configurator/grouped', { params })
    .then((r) => {
      const rows: unknown[] = Array.isArray(r.data) ? r.data : [];
      return rows as ProductGroup[];
    });


export const getCatalogBranchOrder = (): Promise<
  Array<{ id: number; title: string; slug: string; sort_order: number }>
> =>
  api.get('/products/catalog-tree').then((r) => {
    const data = r.data;
    const branches = Array.isArray(data)
      ? data
      : Array.isArray(data?.branches)
        ? data.branches
        : [];
    return branches.map((b: Record<string, unknown>) => ({
      id: Number(b.id ?? 0),
      title: String(b.title ?? ''),
      slug: String(b.slug ?? ''),
      sort_order: Number(b.sort_order ?? 0),
    }));
  }).catch(() => []);

export const getCatalogSubtypes = (category: string): Promise<Array<{ subtype: string; slug: string; min_price?: number }>> =>
  api.get('/products/systems/v2', { params: { category } }).then((r) => {
    const rows: unknown[] = Array.isArray(r.data) ? r.data : [];
    const map = new Map<string, { slug: string; min_price?: number; variants_count: number }>();
    for (const row of rows) {
      const s = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
      const sub = String(s.subcategory ?? '').trim();
      if (!sub) continue;
      const slug = String(s.slug ?? '').trim();
      const count = Number(s.variants_count ?? 0);
      if (isPlaceholderCatalogSubtype(sub, slug, count)) continue;
      const existing = map.get(sub);
      if (!existing || count > existing.variants_count) {
        const mp = s.min_price;
        const minPrice =
          mp != null && mp !== '' && Number.isFinite(Number(mp)) ? Number(mp) : undefined;
        map.set(sub, {
          slug,
          min_price: minPrice,
          variants_count: count,
        });
      }
    }
    return [...map.entries()].map(([subtype, v]) => ({ subtype, slug: v.slug, min_price: v.min_price }));
  }).catch(() => []);

export const getGroupById = (groupId: string, opts?: { tier?: string | null }) => {
  const tierQ =
    opts?.tier != null && String(opts.tier).trim()
      ? `&tier=${encodeURIComponent(String(opts.tier).trim())}`
      : '';
  if (groupId.startsWith('st::')) {
    return (async () => {
        // st::{branchSlug}::{subtypeSlug}
        const parts = groupId.split('::');
        const branchSlug  = parts[1] ?? '';
        const subtypeSlug = parts[2] ?? '';
        // Load V3 grouped data (has price_matrix and variants)
        const allRows: ProductGroup[] = await api
          .get('/v3/configurator/grouped')
          .then((r) => {
            const rows: unknown[] = Array.isArray(r.data) ? r.data : [];
            return rows.map(catalogBranchRowToProductGroup).filter((g): g is ProductGroup => g != null);
          });
        // Find by comparing group_id
        const match = allRows.find((g) => g.group_id === groupId);
        if (match) return match;
        // Fallback: search by slug parts
        const fallbackMatch = allRows.find((g) => {
          const gParts = String(g.group_id).split('::');
          return gParts[2] === subtypeSlug && gParts[1] === branchSlug;
        });
        if (fallbackMatch) return fallbackMatch;
        // fallback: bare slug lookup (V3 with V2 fallback)
        try {
          const v3Res = await api.get(
            `/v3/configurator/by-id?group_id=${encodeURIComponent(subtypeSlug)}${tierQ}`,
          );
          return resolvePayloadTopLevelImageUrl(v3Res.data);
        } catch (v3Err) {
          // Fallback to V2
          const v2Res = await api.get(
            `/products/grouped/by-id?group_id=${encodeURIComponent(subtypeSlug)}${tierQ}`,
          );
          return resolvePayloadTopLevelImageUrl(v2Res.data);
        }
      })();
  }
  if (groupId.startsWith('v2::')) {
    return (async () => {
        const slug = groupId.replace(/^v2::/, '');
        const [systemRes, groupedRes] = await Promise.all([
          api.get(`/products/systems/v2/${encodeURIComponent(slug)}`).then((r) => r.data),
          api.get('/products/grouped').then((r) => mapProductGroupsResolveImageUrl(r.data)),
        ]);
        const adminShort = String(systemRes?.short_description || '').trim();
        const adminFull = String(systemRes?.base_specs?.admin_full_description || '').trim();
        const adminAdvantages = Array.isArray(systemRes?.base_specs?.admin_advantages)
          ? systemRes.base_specs.admin_advantages
          : null;

        const systemsVariants = Array.isArray(systemRes?.variants) ? systemRes.variants : [];
        const grouped = Array.isArray(groupedRes) ? groupedRes : [];
        const wantedCategory = systemRes?.category;
        const wantedSubcategory = systemRes?.subcategory;
        const wantedCode = String(systemRes?.system_code ?? '').toUpperCase();

        const scoreGroup = (g: any): number => {
          let score = 0;
          if (g?.category === wantedCategory) score += 4;
          if ((g?.subcategory ?? null) === (wantedSubcategory ?? null)) score += 3;
          const variants = Array.isArray(g?.variants) ? g.variants : [];
          if (
            wantedCode &&
            variants.some((v: any) => String(v?.motor_model ?? '').toUpperCase().startsWith(wantedCode))
          ) {
            score += 10;
          }
          return score;
        };

        const best = grouped
          .map((g: any) => ({ g, score: scoreGroup(g) }))
          .sort((a: any, b: any) => b.score - a.score)[0];

        const gallery = parseV2SystemMedia(systemRes?.media);
        const galleryCover =
          coverUrlFromV2Gallery(gallery) ||
          (systemRes?.image_url ? resolveBackendMediaUrl(String(systemRes.image_url)) : undefined);

        // Build typed variants from systemRes (V3 data with price_matrix)
        const mappedVariants = systemsVariants.map((v: any, idx: number) => ({
          id: v.id ?? -(idx + 1),
          motor_model: v.motor_model,
          motor_full_name: v.variant_specs?.motor_full_name || v.motor_model,
          protocol: v.protocol,
          interface_type: v.interface_type,
          is_battery_powered: v.is_battery_powered ?? (v.voltage_type === '\u0410\u041a\u0411'),
          color: v.color,
          power_watts: v.variant_specs?.power_watts,
          price_matrix: Array.isArray(v.price_matrix) && v.price_matrix.length > 0
            ? v.price_matrix
            : (Array.isArray(v.dimensions_price_matrix) ? v.dimensions_price_matrix : []),
          subtype_tags: Array.isArray(v.subtype_tags) ? v.subtype_tags : [],
        }));
        const sysMinPrice = (() => {
          if (systemRes?.min_price && Number(systemRes.min_price) > 0) return Number(systemRes.min_price);
          if (Array.isArray(systemRes?.price_matrix) && systemRes.price_matrix.length > 0) {
            const prices = systemRes.price_matrix.map((p: any) => Number(p.price)).filter((p: number) => p > 0);
            if (prices.length) return Math.min(...prices);
          }
          return 0;
        })();
        // Fallback descriptions from best legacy match (even score=0 = same category)
        const legacyShort = best?.g?.short_description || best?.g?.short_summary || best?.g?.marketing_description || '';
        const legacyFull = best?.g?.description || '';
        const legacyAdvantages = best?.g?.advantages;
        // Build V3 specs object for TTX section (exclude service keys)
        const V3_SKIP_KEYS = new Set(['admin_full_description', 'admin_advantages', 'admin_short_description', 'linked_product_group_id']);
        const v3Specs: Record<string, unknown> = {};
        if (systemRes?.base_specs && typeof systemRes.base_specs === 'object') {
          for (const [k, v] of Object.entries(systemRes.base_specs as Record<string, unknown>)) {
            if (!V3_SKIP_KEYS.has(k) && v !== null && v !== undefined && v !== '') v3Specs[k] = v;
          }
        }
        if (best?.g && best.score > 0) {
          return {
            ...best.g,
            group_id: groupId,
            name: systemRes?.title || best.g.name,
            image_url:
              galleryCover ||
              (systemRes?.image_url ? resolveBackendMediaUrl(String(systemRes.image_url)) : undefined) ||
              (best.g.image_url ? resolveBackendMediaUrl(String(best.g.image_url)) : undefined),
            gallery: gallery.length ? gallery : undefined,
            short_summary: (() => {
              const full = adminFull || String(best.g.description || '').trim();
              const short = adminShort || String(best.g.short_summary || best.g.short_description || '').trim();
              if (short && full && short === full) {
                return String(best.g.short_summary || best.g.short_description || '').trim() || null;
              }
              return short || null;
            })(),
            short_description: (() => {
              const full = adminFull || String(best.g.description || '').trim();
              const short = adminShort || String(best.g.short_description || '').trim();
              if (short && full && short === full) return null;
              return short || null;
            })(),
            marketing_description: (() => {
              const full = adminFull || String(best.g.description || '').trim();
              const short = adminShort || String(best.g.marketing_description || '').trim();
              if (short && full && short === full) return null;
              return short || null;
            })(),
            description: adminFull || best.g.description,
            advantages: adminAdvantages || best.g.advantages,
            selector_schema: systemRes?.selector_schema || best.g.selector_schema || {},
            subtype_tags: Array.isArray(systemRes?.subtype_tags) ? systemRes.subtype_tags : (best.g.subtype_tags || []),
            protocols: systemRes?.protocols?.length ? systemRes.protocols : (best.g.protocols || []),
            interface_types: systemRes?.interface_types?.length ? systemRes.interface_types : (best.g.interface_types || []),
            has_battery: systemRes?.has_battery ?? best.g.has_battery,
            has_wired: systemRes?.has_wired ?? best.g.has_wired,
            min_price: sysMinPrice || best.g.min_price || 0,
            price_matrix: Array.isArray(systemRes?.price_matrix) ? systemRes.price_matrix : [],
            full_specs: systemRes?.base_specs || {},
            specs: v3Specs,
            variants: mappedVariants,
          };
        }

        return {
          group_id: groupId,
          name: systemRes?.title || 'Система',
          subcategory: systemRes?.subcategory,
          category: systemRes?.category || 'Каталог',
          image_url: galleryCover || (systemRes?.image_url ? resolveBackendMediaUrl(String(systemRes.image_url)) : undefined),
          gallery: gallery.length ? gallery : undefined,
          is_premium: false,
          protocols: systemRes?.protocols || [],
          interface_types: systemRes?.interface_types || [],
          has_battery: systemRes?.has_battery ?? false,
          has_wired: systemRes?.has_wired ?? true,
          min_price: sysMinPrice,
          price_matrix: Array.isArray(systemRes?.price_matrix) ? systemRes.price_matrix : [],
          selector_schema: systemRes?.selector_schema || {},
          subtype_tags: Array.isArray(systemRes?.subtype_tags) ? systemRes.subtype_tags : [],
          variants: mappedVariants,
          full_specs: systemRes?.base_specs || {},
          specs: v3Specs,
          short_summary: adminShort || legacyShort || null,
          short_description: adminShort || legacyShort || null,
          marketing_description: adminShort || legacyShort || null,
          description: adminFull || legacyFull || null,
          advantages: adminAdvantages || legacyAdvantages || [],
        };
      })();
  }
  return (async () => {
        // Try V3 first, fallback to V2
        try {
          const v3Res = await api.get(
            `/v3/configurator/by-id?group_id=${encodeURIComponent(groupId)}${tierQ}`,
          );
          return resolvePayloadTopLevelImageUrl(v3Res.data);
        } catch (v3Err) {
          const v2Res = await api.get(
            `/products/grouped/by-id?group_id=${encodeURIComponent(groupId)}${tierQ}`,
          );
          return resolvePayloadTopLevelImageUrl(v2Res.data);
        }
      })();
};

/**
 * POST `/api/products/systems/v2/{slug}/price` — тело совпадает с `SystemPriceRequest`
 * в `backend/app/routers/products.py`. Лишние поля Pydantic отбрасывает; для матрицы нужен
 * хотя бы один из length_m / width_m / projection_m при непустой конфигурации.
 */
export const calculateSystemV2Price = (
  systemSlug: string,
  payload: {
    interface_type?: string;
    protocol?: string;
    control_type?: string;
    control_types?: string[];
    tier_slug?: string;
    profile_color?: string;
    motor_color?: string;
    length_m?: number;
    width_m?: number;
    projection_m?: number;
    height_m?: number;
    opening_type?: string;
    color?: string;
    include_remote?: boolean;
    include_sensor?: boolean;
    include_ral?: boolean;
  }
) => api.post(`/products/systems/v2/${encodeURIComponent(systemSlug)}/price`, payload).then((r) => r.data);

/** POST `/api/products/subtype/{id}/price` — FormulaEngine или product.price_matrix. */
export const calculateSubtypePrice = (
  subtypeId: number,
  payload: {
    interface_type?: string;
    protocol?: string;
    control_types?: string[];
    tier_slug?: string;
    profile_color?: string;
    motor_color?: string;
    length_m?: number;
    width_m?: number;
    projection_m?: number;
    opening_type?: string;
    color?: string;
    include_remote?: boolean;
    include_sensor?: boolean;
    include_ral?: boolean;
  }
) => api.post(`/products/subtype/${subtypeId}/price`, payload).then((r) => r.data);

export const getProduct = (id: number) =>
  api.get(`/products/${id}`).then((r) => resolvePayloadTopLevelImageUrl(r.data));

// Cart
export const getCart = (): Promise<CartListResponse> =>
  api.get('/cart/').then((r) => {
    const data = r.data;
    if (Array.isArray(data)) {
      return { items: data, discount_pct: 0 };
    }
    return {
      items: data?.items ?? [],
      discount_pct: Number(data?.discount_pct ?? 0),
    };
  });

export const addToCart = (payload: {
  product_id: number;
  length_m?: number;
  width_m?: number;
  projection_m?: number;
  quantity?: number;
  custom_params?: Record<string, unknown>;
}) => api.post('/cart/', payload).then((r) => r.data);

export const addSystemV2ToCart = (payload: {
  system_slug?: string;
  catalog_variant_id?: number;
  quantity?: number;
  interface_type?: string;
  protocol?: string;
  control_types?: string[];
  tier_slug?: string;
  profile_color?: string;
  motor_color?: string;
  color?: string;
  opening_type?: string;
  length_m?: number;
  width_m?: number;
  projection_m?: number;
  /** Высота проёма и т.п. — уходит в custom_params заказа, на расчёт матрицы не влияет. */
  client_height_m?: number;
  include_remote?: boolean;
  include_sensor?: boolean;
  include_ral?: boolean;
  price_on_request?: boolean;
}) => api.post('/cart/v2', payload).then((r) => r.data);

export const removeFromCart = (itemId: number) =>
  api.delete(`/cart/${itemId}`).then((r) => r.data);

export const clearCart = () => api.delete('/cart/').then((r) => r.data);

// Orders
export type CheckoutUiCopy = {
  address_placeholder: string;
  client_name_placeholder: string;
  client_phone_placeholder: string;
  submit_button: string;
  total_label: string;
  retail_total_label: string;
  savings_label: string;
  price_on_request_hint: string;
  error_address_short: string;
  error_name_short: string;
  error_phone_short: string;
  surcharge_opening_symmetric: string;
  surcharge_opening_oneway: string;
};

export const DEFAULT_CHECKOUT_UI: CheckoutUiCopy = {
  address_placeholder: 'Адрес доставки',
  client_name_placeholder: 'ФИО для договора',
  client_phone_placeholder: 'Контактный телефон',
  submit_button: 'Оформить заказ',
  total_label: 'Итого',
  retail_total_label: 'Розничная сумма',
  savings_label: 'Ваша выгода',
  price_on_request_hint: 'В корзине есть позиции с ценой по запросу. Финальную стоимость уточнит менеджер.',
  error_address_short: 'Укажите адрес доставки (минимум 6 символов).',
  error_name_short: 'Укажите ФИО для договора (минимум 3 символа).',
  error_phone_short: 'Укажите контактный телефон (минимум 10 цифр).',
  surcharge_opening_symmetric: 'Симметричное открывание',
  surcharge_opening_oneway: 'Одностороннее открывание',
};

export const getCheckoutUi = () =>
  api.get<CheckoutUiCopy>('/checkout-ui').then((r) => r.data);

export const getAdminCheckoutUi = () =>
  api.get<CheckoutUiCopy>('/admin/checkout-ui').then((r) => r.data);

export const saveAdminCheckoutUi = (body: CheckoutUiCopy) =>
  withAdminApiRetry(() => api.put<CheckoutUiCopy>('/admin/checkout-ui', body).then((r) => r.data));

export type ProfileInfoItem = {
  id: string;
  label: string;
  sub: string;
  href: string;
};

export type ProfileUiCopy = {
  section_title: string;
  orders_label: string;
  orders_sub: string;
  measurements_label: string;
  measurements_sub: string;
  manager_label: string;
  manager_sub: string;
  manager_href: string;
  admin_label: string;
  admin_sub: string;
  admin_sub_pending: string;
  partner_banner_title: string;
  partner_banner_sub: string;
  partner_active_title: string;
  partner_active_sub: string;
  info_items: ProfileInfoItem[];
};

export const DEFAULT_PROFILE_UI: Omit<ProfileUiCopy, 'info_items'> = {
  section_title: '◈ О компании Onviz',
  orders_label: 'Мои заказы',
  orders_sub: 'Статусы, суммы и детали заказов',
  measurements_label: 'Мои замеры',
  measurements_sub: 'Запись и история заявок на замер',
  manager_label: 'Связаться с менеджером',
  manager_sub: 'Консультация и поддержка в Telegram',
  manager_href: 'https://t.me/onvizmsk',
  admin_label: 'Панель управления',
  admin_sub: 'Заказы, клиенты, статусы',
  admin_sub_pending: 'Есть новые заказы — перейдите в админку для обработки',
  partner_banner_title: 'Партнёрская программа 20%',
  partner_banner_sub: 'Тапните для активации по коду',
  partner_active_title: 'Партнёрская программа',
  partner_active_sub: 'Скидка {pct}% на все заказы',
};

export type RulonNavOption = { id: string; label: string; hint: string };

export type RulonNavigatorUiCopy = {
  install_types: RulonNavOption[];
  series: RulonNavOption[];
  fabric_lines: RulonNavOption[];
};

export const DEFAULT_RULON_NAVIGATOR_UI: RulonNavigatorUiCopy = {
  install_types: [
    { id: 'Открытая', label: 'Открытая', hint: 'Классический рулон: ткань на открытом валу, компактно и универсально для жилых комнат.' },
    { id: 'В коробе', label: 'В коробе', hint: 'Алюминиевый короб скрывает механизм и защищает ткань от выгорания — аккуратный вид у окна.' },
    { id: 'Габаритный', label: 'Габаритный', hint: 'Для крупных проёмов: усиленный профиль и привод под большие ширины.' },
    { id: 'UNI-3', label: 'UNI-3', hint: 'Система UNI-3: компактный корпус и готовые решения под стандартные проёмы.' },
  ],
  series: [
    { id: '38 мм', label: '38 мм', hint: 'Узкий профиль — для небольших и средних окон.' },
    { id: '50 мм', label: '50 мм', hint: 'Универсальный диаметр для большинства помещений.' },
    { id: '65 мм', label: '65 мм', hint: 'Усиленный вал — для широких и тяжёлых полотен.' },
  ],
  fabric_lines: [
    { id: 'Классическая', label: 'Классическая', hint: 'Одно полотно на валу — стандартное затемнение и простое управление.' },
    { id: 'День-ночь', label: 'День-ночь', hint: 'Чередующиеся полосы: регулируете свет без полного закрытия окна.' },
  ],
};

export const getRulonNavigatorUi = () =>
  api.get<RulonNavigatorUiCopy>('/rulon-navigator-ui').then((r) => r.data);

export const getAdminRulonNavigatorUi = () =>
  api.get<RulonNavigatorUiCopy>('/admin/rulon-navigator-ui').then((r) => r.data);

export const saveAdminRulonNavigatorUi = (body: RulonNavigatorUiCopy) =>
  withAdminApiRetry(() =>
    api.put<RulonNavigatorUiCopy>('/admin/rulon-navigator-ui', body).then((r) => r.data),
  );

export type ConfiguratorStepCopyRow = { title: string; hint: string; icon: string };

export type ConfiguratorStepCopyPayload = {
  step_copy: Record<string, ConfiguratorStepCopyRow>;
};

export const DEFAULT_CONFIGURATOR_STEP_COPY: ConfiguratorStepCopyPayload = {
  step_copy: {
    tier: {
      icon: '✦',
      title: 'Профиль исполнения',
      hint: 'Standard — базовая несущая способность и стандартный шум. Premium — усиленный профиль и запас по нагрузке для широких проёмов.',
    },
    power: {
      icon: '⚡',
      title: 'Питание мотора',
      hint: '220В — постоянная сеть, максимальная тяга и любые длины. АКБ — автономный мотор на батарее, обычно ZigBee; для длинных штор выбирайте сеть.',
    },
    opening: {
      icon: '↔',
      title: 'Направление открывания',
      hint: 'К центру — две створки навстречу. Влево / вправо — одна створка уезжает в сторону. Влияет на комплектацию направляющих.',
    },
    control: {
      icon: '📡',
      title: 'Управление и подключение',
      hint: 'Протокол умного дома — один из Wi‑Fi / ZigBee / Matter. Фаза без Wi‑Fi — мотор MR‑2234F4. Фаза + RS‑485 / TUYA — линейка MR‑2234F5.',
    },
    profile_color: {
      icon: '🎨',
      title: 'Цвет профиля',
      hint: 'Цвет алюминиевой шины. Подбирается под потолок и стены — в прайсе отдельные позиции для белого и чёрного.',
    },
    motor_color: {
      icon: '⚙',
      title: 'Цвет мотора',
      hint: 'Корпус привода в зоне видимости. Белый и чёрный — в каталоге; серебро — там, где указано в матрице.',
    },
    size: {
      icon: '📏',
      title: 'Размер проёма',
      hint: 'Ширина в метрах по прайсу. Цена внизу пересчитается сразу после выбора длины.',
    },
  },
};

export const getConfiguratorStepCopy = () =>
  api.get<ConfiguratorStepCopyPayload>('/configurator-step-copy').then((r) => r.data);

export const getAdminConfiguratorStepCopy = () =>
  api.get<ConfiguratorStepCopyPayload>('/admin/configurator-step-copy').then((r) => r.data);

export const saveAdminConfiguratorStepCopy = (body: ConfiguratorStepCopyPayload) =>
  api.put<ConfiguratorStepCopyPayload>('/admin/configurator-step-copy', body).then((r) => r.data);

export type SmartHomePageCopy = {
  title: string;
  subtitle: string;
  ecosystems_section_title: string;
  protocols_section_title: string;
  chooser_section_title: string;
};

export type SmartHomeEcosystemCopy = {
  id: string;
  name: string;
  icon: string;
  tagline: string;
  protocols: string[];
  description: string;
  how_to: string[];
  requirements: string[];
  limitations: string[];
};

export type SmartHomeProtocolCopy = { id: string; name: string; desc: string };

export type SmartHomeChooserCopy = { id: string; q: string; a: string };

export type SmartHomeUiCopy = {
  page: SmartHomePageCopy;
  ecosystems: SmartHomeEcosystemCopy[];
  protocols: SmartHomeProtocolCopy[];
  chooser: SmartHomeChooserCopy[];
};

export const DEFAULT_SMART_HOME_UI: SmartHomeUiCopy = {
  page: {
    title: 'Умный дом',
    subtitle: 'Совместимость с экосистемами и протоколами',
    ecosystems_section_title: '◈ Экосистемы',
    protocols_section_title: '◈ Протоколы',
    chooser_section_title: '◈ Как выбрать протокол?',
  },
  ecosystems: [
    {
      id: 'xiaomi',
      name: 'Xiaomi Home',
      icon: '🏠',
      tagline: 'Wi-Fi управление через приложение Xiaomi',
      protocols: ['Wi-Fi'],
      description:
        'Xiaomi Home — мобильное приложение и платформа для управления всеми устройствами экосистемы Xiaomi. Управляйте электрокарнизами, лампочками, кондиционерами и другими устройствами из одного приложения.',
      how_to: [
        'Установите приложение «Xiaomi Home» (iOS / Android)',
        'Войдите в Mi-аккаунт или создайте новый',
        'Убедитесь, что Wi-Fi роутер работает на частоте 2,4 ГГц',
        'Добавьте карниз через «+» → Поиск устройств',
      ],
      requirements: ['Смартфон iOS / Android', 'Wi-Fi 2,4 ГГц', 'Mi-аккаунт'],
      limitations: [],
    },
    {
      id: 'tuya',
      name: 'Smart Life / Tuya',
      icon: '📱',
      tagline: 'Wi-Fi и ZigBee через Smart Life',
      protocols: ['Wi-Fi', 'ZigBee'],
      description:
        'Tuya — IoT-платформа, на основе которой работает приложение Smart Life. Поддерживает подключение через Wi-Fi и ZigBee. При использовании ZigBee-шлюза сценарии работают даже без интернета.',
      how_to: [
        'Установите «Tuya Smart» или «Smart Life» (iOS / Android)',
        'Зарегистрируйтесь и войдите в аккаунт',
        'Для Wi-Fi: подключите карниз к сети 2,4 ГГц',
        'Для ZigBee: сначала добавьте ZigBee-шлюз, затем карниз',
        'Настройте сценарии и таймеры по расписанию',
      ],
      requirements: ['Смартфон iOS / Android', 'Wi-Fi 2,4 ГГц', 'Для ZigBee — ZigBee-шлюз'],
      limitations: [
        'Без интернета Wi-Fi-устройства недоступны удалённо',
        'ZigBee-сценарии работают локально без интернета',
      ],
    },
    {
      id: 'alice',
      name: 'Алиса (Яндекс)',
      icon: '🎙',
      tagline: 'Голосовое управление через Алису',
      protocols: ['Wi-Fi (MiHome)', 'Wi-Fi (TUYA)'],
      description:
        'Умный дом Яндекса с управлением голосом через Алису. Подключение через фирменное приложение производителя с привязкой к «Дому с Алисой».',
      how_to: [
        'Настройте карниз в приложении Xiaomi Home или Smart Life',
        'Откройте «Дом с Алисой»',
        'Нажмите «Добавить устройство» → выберите бренд',
        'Введите данные аккаунта Xiaomi Home или Tuya для привязки',
        'Управляйте голосом: «Алиса, закрой шторы в спальне»',
      ],
      requirements: [],
      limitations: ['ZigBee и Matter напрямую к колонке не подключаются', 'Интеграция через Xiaomi Home или Tuya'],
    },
    {
      id: 'matter',
      name: 'Matter',
      icon: '🌐',
      tagline: 'Единый стандарт: Apple, Google, Amazon',
      protocols: ['Matter'],
      description:
        'Matter — открытый стандарт умного дома, позволяющий устройствам разных производителей работать в единой системе. Работает через Wi-Fi или Thread, поддерживает локальное управление без интернета.',
      how_to: [
        'Отсканируйте QR-код устройства смартфоном',
        'Устройство автоматически подключается к домашней сети',
        'Управляйте через Apple Home, Google Home или Amazon Alexa',
        'Создавайте сценарии: «В 8:00 открыть шторы в спальне»',
      ],
      requirements: ['Поддерживаемый хаб (HomePod, Nest Hub, Echo)', 'Wi-Fi или Thread-сеть'],
      limitations: [],
    },
    {
      id: 'homeassistant',
      name: 'Home Assistant',
      icon: '🖥',
      tagline: 'Локальная автоматизация без облака',
      protocols: ['ZigBee', 'Wi-Fi (TUYA)', 'RS-485'],
      description:
        'Home Assistant — локальная платформа автоматизации с открытым кодом. Работает на Raspberry Pi или мини-ПК. Полный контроль без облачных сервисов. Поддерживает ZigBee, Wi-Fi (Tuya Local), RS-485 и другие протоколы.',
      how_to: [
        'Установите Home Assistant OS на Raspberry Pi или мини-ПК',
        'Откройте веб-интерфейс и добавьте интеграцию ZigBee (ZHA или Zigbee2MQTT)',
        'Или добавьте интеграцию Tuya Local для Wi-Fi устройств',
        'Для RS-485 настройте Modbus-интеграцию',
        'Создавайте автоматизации через визуальный редактор',
      ],
      requirements: ['Raspberry Pi 4 или аналог', 'Для ZigBee — USB ZigBee-стик'],
      limitations: [],
    },
    {
      id: 'wirenboard',
      name: 'WirenBoard',
      icon: '⚙️',
      tagline: 'Промышленная автоматизация RS-485',
      protocols: ['RS-485', 'ZigBee'],
      description:
        'Wiren Board — профессиональный контроллер на базе Linux для домашней и промышленной автоматизации. Поддерживает RS-485 (Modbus), ZigBee и сухие контакты.',
      how_to: [
        'Подключите карниз к модулю RS-485 на Wiren Board (витая пара A/B, GND)',
        'В веб-интерфейсе: «Modbus» → «Добавить устройство»',
        'Укажите адрес и регистры управления',
        'Или установите ZigBee-модуль WBE2R-R-ZIGBEE и добавьте устройство через ZHA/MQTT',
      ],
      requirements: [],
      limitations: [
        'Сухие контакты: нет обратной связи о положении штор',
        'Требуется настройка Modbus-регистров',
      ],
    },
  ],
  protocols: [
    { id: 'wifi', name: 'Wi-Fi', desc: 'Подключение напрямую к домашнему роутеру. Управление из любой точки мира. Wi-Fi 2,4 ГГц.' },
    { id: 'zigbee', name: 'ZigBee', desc: 'Mesh-сеть, низкое энергопотребление. Сценарии без интернета. Лучший выбор для АКБ-моторов.' },
    { id: 'matter', name: 'Matter', desc: 'Открытый стандарт — Apple, Google, Amazon. Локальное управление без облака. QR-код.' },
    { id: 'rs485', name: 'RS-485', desc: 'Проводной протокол для коммерции. WirenBoard, KNX, Crestron и другие системы.' },
  ],
  chooser: [
    { id: 'xiaomi', q: 'Уже есть Xiaomi устройства', a: 'Wi-Fi (MiHome)' },
    { id: 'tuya', q: 'Используете Smart Life / Tuya', a: 'Wi-Fi (TUYA) или ZigBee' },
    { id: 'voice', q: 'Нужна Алиса / Siri / Google', a: 'Matter или Wi-Fi' },
    { id: 'ha', q: 'Home Assistant / WirenBoard', a: 'ZigBee или RS-485' },
    { id: 'rf', q: 'Просто пульт без умного дома', a: 'RF 433 МГц' },
  ],
};

export const getSmartHomeUi = () => api.get<SmartHomeUiCopy>('/smart-home-ui').then((r) => r.data);

export const getAdminSmartHomeUi = () => api.get<SmartHomeUiCopy>('/admin/smart-home-ui').then((r) => r.data);

export const saveAdminSmartHomeUi = (body: SmartHomeUiCopy) =>
  api.put<SmartHomeUiCopy>('/admin/smart-home-ui', body).then((r) => r.data);

export const DEFAULT_PROFILE_INFO_ITEMS: ProfileInfoItem[] = [
  { id: 'about', label: 'О компании', sub: 'Onviz — производитель моторизированных систем управления', href: 'https://onviz.ru/about-us' },
  { id: 'warranty', label: 'Гарантия 10 лет', sub: 'На все изделия — записано в договоре', href: 'https://onviz.ru/garantiya-na-produkciyu-onviz' },
  { id: 'delivery', label: 'Доставка', sub: 'По всей России — СДЭК, почта, курьер', href: 'https://onviz.ru/delivery' },
  { id: 'install', label: 'Монтаж', sub: 'Профессиональный монтаж в Москве и МО', href: 'https://onviz.ru/service' },
  { id: 'support', label: 'Поддержка', sub: 'Отвечаем через Telegram каждый день', href: 'https://t.me/onvizmsk' },
  { id: 'faq', label: 'FAQ', sub: 'Ответы на частые вопросы', href: 'https://onviz.ru/chavo-roznica' },
  { id: 'service', label: 'Сервисный центр', sub: 'Техподдержка и настройка изделий', href: 'https://onviz.ru/servisnaya-podderzhka-onviz' },
];

export const getProfileUi = () =>
  api.get<ProfileUiCopy>('/profile-ui').then((r) => r.data);

export const getAdminProfileUi = () =>
  api.get<ProfileUiCopy>('/admin/profile-ui').then((r) => r.data);

export const saveAdminProfileUi = (body: ProfileUiCopy) =>
  withAdminApiRetry(() => api.put<ProfileUiCopy>('/admin/profile-ui', body).then((r) => r.data));

export const createOrder = (payload: {
  delivery_address: string;
  client_name: string;
  client_phone: string;
  notes?: string;
}) => api.post('/orders/', payload).then((r) => r.data);

export const getMyOrders = () => api.get('/orders/my').then((r) => r.data);

// Partner
export const activatePartnerCode = (code: string) =>
  api.post('/auth/activate', { code }).then((r) => r.data);

// Cart recalculation after role change
export const recalculateCart = () =>
  api.post('/cart/recalculate').then((r) => r.data);

// Catalog
export const getKnowledgeBase = () => api.get('/catalog/knowledge-base').then((r) => r.data);
export const getCatalogV2 = () => api.get('/catalog/v2').then((r) => r.data);
export const getCatalogV2Item = (article: string) =>
  api.get('/catalog/v2/item', { params: { article } }).then((r) => r.data);
export const getRalSurcharges = () => api.get('/catalog/ral-surcharges').then((r) => r.data);
export const getAccessories = () =>
  api.get('/products/accessories/all').then((r) => mapProductGroupsResolveImageUrl(r.data));
export const getServices = () => api.get('/products/services/all').then((r) => r.data);
export const getMotorsCatalog = () => api.get('/products/motors/all').then((r) => r.data);
export type MotorHandbookMediaMap = Record<string, string>;
export const getMotorHandbookMedia = (): Promise<MotorHandbookMediaMap> =>
  api
    .get<{ items: MotorHandbookMediaMap }>('/motor-handbook/media')
    .then((r) => (r.data && typeof r.data === 'object' && r.data.items ? r.data.items : {}));
export const getAdminMotorHandbookMedia = (): Promise<MotorHandbookMediaMap> =>
  api
    .get<{ items: MotorHandbookMediaMap }>('/admin/motor-handbook/media')
    .then((r) => (r.data && typeof r.data === 'object' && r.data.items ? r.data.items : {}));
export const saveAdminMotorHandbookMedia = (items: MotorHandbookMediaMap): Promise<MotorHandbookMediaMap> =>
  api
    .put<{ items: MotorHandbookMediaMap }>('/admin/motor-handbook/media', { items })
    .then((r) => (r.data && typeof r.data === 'object' && r.data.items ? r.data.items : {}));
export const uploadAdminMotorHandbookImage = (
  modelKey: string,
  file: File,
): Promise<MotorHandbookMediaMap> => {
  const form = new FormData();
  form.append('uploaded', file);
  return api
    .post<{ items: MotorHandbookMediaMap }>(`/admin/motor-handbook/media/${encodeURIComponent(modelKey)}/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => (r.data && typeof r.data === 'object' && r.data.items ? r.data.items : {}));
};

export type MotorHandbookCopyMap = Record<string, string>;
export const getMotorHandbookCopy = (): Promise<MotorHandbookCopyMap> =>
  api
    .get<{ items: MotorHandbookCopyMap }>('/motor-handbook/copy')
    .then((r) => (r.data && typeof r.data === 'object' && r.data.items ? r.data.items : {}));
export const getAdminMotorHandbookCopy = (): Promise<MotorHandbookCopyMap> =>
  api
    .get<{ items: MotorHandbookCopyMap }>('/admin/motor-handbook/copy')
    .then((r) => (r.data && typeof r.data === 'object' && r.data.items ? r.data.items : {}));
export const saveAdminMotorHandbookCopy = (items: MotorHandbookCopyMap): Promise<MotorHandbookCopyMap> =>
  api
    .put<{ items: MotorHandbookCopyMap }>('/admin/motor-handbook/copy', { items })
    .then((r) => (r.data && typeof r.data === 'object' && r.data.items ? r.data.items : {}));
export const addAccessoryToCart = (accessory_id: number, quantity = 1) =>
  api.post('/cart/accessory', { accessory_id, quantity }).then((r) => r.data);

export interface LinkedMotor {
  id: number;
  title: string;
  slug: string;
  is_default: boolean;
  is_available: boolean;
  price_override?: number;
}

export interface PriceTier {
  id: number;
  label?: string;
  width_min_cm?: number;
  width_max_cm?: number;
  height_min_cm?: number;
  height_max_cm?: number;
  base_price: number;
  price_per_cm_width?: number;
  price_per_cm_height?: number;
}

export interface AdminCatalogSystem {
  id: number;
  tenant_id?: string;
  // SSOT: Foreign Keys (category/subcategory deprecated)
  branch_id?: number;
  subtype_id?: number;
  // Deprecated: text fields (use branch_id/subtype_id)
  category_id: number;
  category?: string;
  subcategory?: string;
  slug: string;
  title: string;
  short_description?: string;
  full_description?: Record<string, unknown>;
  image_url?: string;
  base_image_url?: string;
  sort_order?: number;
  is_active: boolean;
  status?: string;
  created_at?: string;
  updated_at?: string;
  advantages?: string[];
  protocols?: string[];
  selector_schema?: Record<string, unknown>;
  // Linked data (loaded via include_* params)
  motors?: LinkedMotor[];
  price_tiers?: PriceTier[];
}

export const getAdminCatalogSystems = (params?: { search?: string; category?: string; skip?: number; limit?: number }): Promise<{ items: AdminCatalogSystem[]; total: number }> =>
  api.get('/admin/catalog-systems', { params }).then((r) => r.data);

/** SSOT: Strict filtering by subtype_id. Returns ONLY systems belonging to this subtype. */
export const getAdminCatalogSystemsBySubtype = (subtypeId: number, options?: { includeMotors?: boolean; includePriceTiers?: boolean }): Promise<AdminCatalogSystem[]> =>
  api.get('/admin/catalog-systems-by-subtype', { 
    params: { 
      subtype_id: subtypeId,
      include_motors: options?.includeMotors ?? false,
      include_price_tiers: options?.includePriceTiers ?? false,
    } 
  }).then((r) => r.data);

export const updateAdminCatalogSystem = (
  systemId: number,
  payload: {
    title?: string;
    short_description?: string;
    full_description?: string;
    advantages?: string[];
    image_url?: string;
    is_active?: boolean;
  }
) => api.patch(`/admin/catalog-systems/${systemId}`, payload).then((r) => r.data);

export const updateAdminGroupContent = (payload: {
  group_id: string;
  short_description?: string;
  full_description?: string;
  advantages?: string[];
  image_url?: string | null;
  tier_slug?: string | null;
}) => api.patch('/admin/catalog-content/by-group', payload).then((r) => r.data);

export const resolveAdminCatalogSystem = (payload: {
  slug?: string;
  title: string;
  category?: string;
  subcategory?: string;
  short_description?: string;
  image_url?: string;
  /** Совпадает с group_id карточки — API отдаёт ту же медиатеку всем клиентам. */
  product_group_id?: string;
}) => api.post('/admin/catalog-systems/resolve', payload).then((r) => r.data);

export const uploadAdminCatalogSystemImage = (systemId: number, file: File) => {
  const form = new FormData();
  form.append('image', file);
  return api.post(`/admin/catalog-systems/${systemId}/image`, form).then((r) => r.data);
};

export const deleteAdminCatalogSystemImage = (systemId: number) =>
  api.delete(`/admin/catalog-systems/${systemId}/image`).then((r) => r.data);

/** Список медиа v2 (админ). Ответ — JSON-массив строк медиа (FastAPI). */
export const getAdminCatalogSystemMedia = (systemId: number) =>
  api.get(`/admin/catalog-systems/${systemId}/media`).then((r) => {
    const raw = r.data as unknown;
    const arr = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object' && Array.isArray((raw as { media?: unknown }).media)
        ? (raw as { media: unknown[] }).media
        : [];
    return parseV2SystemMedia(arr);
  });

/** Импорт уже известных URL в медиатеку без повторной заливки файлов (связать карусель с БД). */
export const importAdminCatalogSystemMediaUrls = (
  systemId: number,
  payload: { urls: string[]; replace?: boolean },
) =>
  api
    .post(`/admin/catalog-systems/${systemId}/media/import-urls`, payload)
    .then((r) => parseV2SystemMedia(r.data));

export const appendAdminCatalogSystemMedia = (systemId: number, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/admin/catalog-systems/${systemId}/media`, form).then((r) => r.data);
};

export const replaceAdminCatalogSystemMedia = (systemId: number, mediaId: number, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api
    .post(`/admin/catalog-systems/${systemId}/media/${mediaId}/replace`, form)
    .then((r) => r.data);
};

export const deleteAdminCatalogSystemMediaItem = (systemId: number, mediaId: number) =>
  api.delete(`/admin/catalog-systems/${systemId}/media/${mediaId}`).then((r) => r.data);

export const reorderAdminCatalogSystemMedia = (systemId: number, orderedMediaIds: number[]) =>
  api
    .patch(`/admin/catalog-systems/${systemId}/media/reorder`, { ordered_media_ids: orderedMediaIds })
    .then((r) => r.data);

export const setAdminCatalogSystemMediaPrimary = (systemId: number, mediaId: number) =>
  api.post(`/admin/catalog-systems/${systemId}/media/${mediaId}/primary`).then((r) => r.data);

export const sendMotorWizardLead = (payload: {
  width_m: number;
  height_m: number;
  estimated_load_kg: number;
  fabric_density: string;
  layers: string;
  preferred_power: string;
  preferred_protocol: string;
  top_recommendations: string[];
  premium_pick?: string;
  note?: string;
}) => api.post('/measurements/motor-wizard-lead', payload).then((r) => r.data);

export const createMeasurementRequest = (payload: {
  city: string;
  phone: string;
  preferred_time: string;
  notes?: string;
}) => api.post('/measurements/', payload).then((r) => r.data);

export const getMyMeasurements = () => api.get('/measurements/my').then((r) => r.data);

export const getAssignedMeasurements = () => api.get('/measurements/assigned').then((r) => r.data);

export const claimAdminOrder = (orderId: number) =>
  api.post(`/admin/orders/${orderId}/claim`).then((r) => r.data);

export type AdminOrderCrmStats = {
  pending: number;
  confirmed: number;
  in_production: number;
  ready: number;
  delivered: number;
  cancelled: number;
  total_orders: number;
  active_orders: number;
  revenue_total: number;
  revenue_month: number;
  unassigned_orders: number;
  scope_manager_id?: number | null;
};

export const getAdminOrderCrmStats = (params?: { manager_id?: number; unassigned?: boolean }) =>
  api.get<AdminOrderCrmStats>('/admin/orders/stats', { params }).then((r) => r.data);

export type AdminManagerBrief = ManagerCrmBrief & { role?: string };

export const getAdminManagers = () =>
  api.get<AdminManagerBrief[]>('/admin/managers').then((r) => r.data);

export const assignAdminOrderManager = (orderId: number, managerId: number | null) =>
  api.patch(`/admin/orders/${orderId}/assign-manager`, { manager_id: managerId }).then((r) => r.data);

export type AdminSystemOverview = {
  ok: boolean;
  orders_count: number;
  users_count: number;
  leads_open_count: number;
  last_order_at?: string | null;
  orders_readable: boolean;
  schema_hint?: string | null;
  is_empty_database: boolean;
  checked_at?: string;
};

export const getAdminSystemOverview = () =>
  api.get<AdminSystemOverview>('/admin/system/overview').then((r) => r.data);

/** Только platform owner — безвозвратное удаление заказа */
export const purgeAdminOrder = (orderId: number) =>
  api.delete<{ ok: boolean; deleted_order_id: number; order_number: string }>(
    `/admin/orders/${orderId}/purge`,
  ).then((r) => r.data);

export type ResetSalesDemoResult = {
  ok: boolean;
  orders_deleted: number;
  order_numbers_sample: string[];
  leads_deleted: number;
  tasks_deleted: number;
  activities_deleted: number;
  messages_deleted: number;
};

/** Только platform owner — полный сброс заказов и CRM для чистого пилота */
export const resetSalesDemoEnvironment = (confirm: string) =>
  api
    .post<ResetSalesDemoResult>('/admin/system/reset-sales-demo', { confirm })
    .then((r) => r.data);

export type ManagerCrmBrief = { id: number; first_name?: string | null; username?: string | null };

export type ManagerCrmLeaderboardRow = AdminOrderCrmStats & { manager: ManagerCrmBrief | null };

export type AdminManagerCrmLeaderboard = {
  managers: ManagerCrmLeaderboardRow[];
  unassigned: AdminOrderCrmStats;
  totals: AdminOrderCrmStats;
};

export const getAdminManagerCrmLeaderboard = () =>
  api.get<AdminManagerCrmLeaderboard>('/admin/orders/stats/managers').then((r) => r.data);

// ─── CRM Core ─────────────────────────────────────────────────────────────

export type SalesStage =
  | 'lead_received'
  | 'negotiation'
  | 'invoice_sent'
  | 'paid'
  | 'measurement'
  | 'measurement_done'
  | 'compose'
  | 'production'
  | 'ready_to_ship'
  | 'fulfillment'
  | 'lost';

export type CrmPipelineCard = {
  id: number;
  order_number: string;
  status: string | null;
  sales_stage: SalesStage;
  final_amount: number;
  assigned_manager_id: number | null;
  days_in_stage: number;
  has_open_task: boolean;
  has_valid_open_task: boolean;
  has_overdue_task: boolean;
  user: {
    id: number;
    telegram_id?: number | null;
    first_name?: string | null;
    username?: string | null;
    phone?: string | null;
    company?: string | null;
    role?: string | null;
  };
  sales_stage_history?: Array<Record<string, unknown>>;
  bitrix_deal_id?: string | null;
  bitrix_sync_error?: string | null;
  delivery_address?: string | null;
  notes?: string | null;
  client_contact?: {
    name: string;
    phone?: string | null;
    company?: string | null;
    telegram_id?: number | null;
    username?: string | null;
  };
  measurement_photo_count?: number;
  has_measurement_report?: boolean;
  order_has_pricing_drift?: boolean;
};

export type CrmOrderBoardCard = {
  order: {
    id: number;
    order_number: string;
    status: string | null;
    sales_stage: string | null;
    total_amount: number;
    discount_pct: number;
    final_amount: number;
    delivery_address?: string | null;
    notes?: string | null;
    manager_notes?: string | null;
    production_notes?: string | null;
    created_at?: string | null;
  };
  client: {
    id: number;
    name: string;
    phone?: string | null;
    telegram_id?: number | null;
    username?: string | null;
    role: string;
  };
  manager?: { id: number; name: string } | null;
  line_items: Array<{
    id: number;
    name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    detail_lines: string[];
    pricing_changed?: boolean;
  }>;
  order_has_pricing_drift?: boolean;
  measurements: Array<{
    id: number;
    status: string | null;
    city?: string | null;
    phone?: string | null;
    address?: string | null;
    preferred_time?: string | null;
    report_notes?: string | null;
    completed_at?: string | null;
    order_id?: number | null;
    assigned_measurer?: { id: number; name: string } | null;
    attachment_count: number;
  }>;
  attachments: Array<{
    id: number;
    measurement_id: number;
    file_url: string;
    caption?: string | null;
    kind: string;
    created_at?: string | null;
  }>;
  can_create_measurement?: boolean;
  suggested_measurement?: { city?: string | null; phone?: string | null };
};

export const ensureCrmOrderMeasurement = (
  orderId: number,
  payload?: { city?: string; preferred_time?: string; notes?: string },
) =>
  api
    .post<{ ok: boolean; measurement_id: number; board_card: CrmOrderBoardCard }>(
      `/admin/crm/orders/${orderId}/ensure-measurement`,
      payload ?? {},
    )
    .then((r) => r.data);

export const getCrmOrderBoardCard = (orderId: number) =>
  api.get<CrmOrderBoardCard>(`/admin/crm/orders/${orderId}/board-card`).then((r) => r.data);

export const getCrmOrderPipelineCard = (orderId: number) =>
  api.get<CrmPipelineCard>(`/admin/crm/orders/${orderId}/pipeline-card`).then((r) => r.data);

export const getCrmLeadCard = (leadId: number) =>
  api.get<CrmLeadCard>(`/admin/crm/leads/${leadId}`).then((r) => r.data);

export type CrmCatalogSuggestItem = {
  kind: 'product' | 'catalog_system';
  label: string;
  subtitle?: string;
  unit_price: number;
  product_id?: number;
  system_slug?: string;
};

export const getCrmCatalogSuggest = (q: string, limit = 12) =>
  api
    .get<{ items: CrmCatalogSuggestItem[] }>('/admin/crm/catalog-suggest', { params: { q, limit } })
    .then((r) => r.data.items);

export const importManagerCartToCrmOrder = (orderId: number) =>
  api
    .post<{ ok: boolean; added: number; final_amount: number; order_number: string }>(
      `/admin/crm/orders/${orderId}/import-cart`,
    )
    .then((r) => r.data);

export type CrmLeadCard = {
  id: number;
  title: string;
  phone?: string | null;
  telegram_id?: number | null;
  user_id?: number | null;
  source: string;
  source_ref_id?: number | null;
  sales_stage: SalesStage;
  assigned_manager_id?: number | null;
  notes?: string | null;
  client_name?: string | null;
  card_type: 'lead';
  has_valid_open_task?: boolean;
  has_overdue_task?: boolean;
  is_converted?: boolean;
};

export type CrmPipelineResponse = {
  stages: SalesStage[];
  columns: Record<string, CrmPipelineCard[]>;
  leads?: CrmLeadCard[];
};

export const getCrmPipeline = (params?: {
  mine_only?: boolean;
  manager_id?: number;
  include_unassigned?: boolean;
  crm_department?: string;
}) => api.get<CrmPipelineResponse>('/admin/crm/pipeline', { params }).then((r) => r.data);

export type CrmActivity = {
  id: number;
  order_id?: number | null;
  lead_id?: number | null;
  kind: string;
  body: string;
  author_name?: string | null;
  contact_date?: string | null;
  visibility: string;
  created_at?: string | null;
  is_legacy?: boolean;
};

export type CrmActivitiesResponse = {
  by_date: Record<string, CrmActivity[]>;
  production_notes?: string | null;
};

export const getCrmOrderActivities = (orderId: number) =>
  api.get<CrmActivitiesResponse>(`/admin/crm/orders/${orderId}/activities`).then((r) => r.data);

export const postCrmOrderActivity = (
  orderId: number,
  payload: { body: string; kind?: string; visibility?: string },
) => api.post<CrmActivity>(`/admin/crm/orders/${orderId}/activities`, payload).then((r) => r.data);

export const patchCrmProductionNotes = (orderId: number, production_notes: string | null) =>
  api
    .patch(`/admin/crm/orders/${orderId}/production-notes`, { production_notes })
    .then((r) => r.data);

export type CrmMyDay = {
  due_today: Array<
    CrmTask & { order_number?: string | null; lead_title?: string | null; client_phone?: string | null }
  >;
  overdue: Array<
    CrmTask & { order_number?: string | null; lead_title?: string | null; client_phone?: string | null }
  >;
  orders_without_task: Array<{ id: number; order_number: string }>;
  leads_without_task: CrmLeadCard[];
};

export const getCrmMyDay = (params?: { manager_id?: number }) =>
  api.get<CrmMyDay>('/admin/crm/my-day', { params }).then((r) => r.data);

export type CrmIntakeItem = CrmLeadCard & {
  last_message_preview?: string | null;
  username?: string | null;
};

export type CrmIntakeResponse = {
  items: CrmIntakeItem[];
  count: number;
  unassigned_count: number;
};

export const getCrmIntake = (params?: { manager_id?: number; crm_department?: string }) =>
  api.get<CrmIntakeResponse>('/admin/crm/intake', { params }).then((r) => r.data);

export const claimCrmLead = (leadId: number) =>
  api.post<{ ok: boolean; lead: CrmLeadCard }>(`/admin/crm/leads/${leadId}/claim`).then((r) => r.data);

export type CrmInsights = {
  days: number;
  manager_id?: number | null;
  leads_created: number;
  leads_converted: number;
  lead_conversion_pct: number;
  orders_in_period: number;
  paid_count: number;
  lost_count: number;
  win_rate_pct: number;
  revenue_paid: number;
  overdue_open_tasks: number;
  avg_days_in_stage: Record<string, number>;
  leads_without_task_pct?: number;
  avg_hours_to_first_call?: number | null;
};

export const getCrmInsights = (params?: { days?: number; manager_id?: number }) =>
  api.get<CrmInsights>('/admin/crm/insights', { params }).then((r) => r.data);

export const getCrmLeadTasks = (leadId: number) =>
  api.get<CrmTask[]>(`/admin/crm/leads/${leadId}/tasks`).then((r) => r.data);

export const createCrmLeadTask = (
  leadId: number,
  payload: { kind?: string; title: string; notes?: string; due_at: string; assignee_id?: number },
) => api.post<CrmTask>(`/admin/crm/leads/${leadId}/tasks`, payload).then((r) => r.data);

export const patchCrmLeadTask = (
  leadId: number,
  taskId: number,
  payload: { status?: string; title?: string; notes?: string; due_at?: string },
) => api.patch<CrmTask>(`/admin/crm/leads/${leadId}/tasks/${taskId}`, payload).then((r) => r.data);

export const getCrmLeadActivities = (leadId: number) =>
  api.get<{ by_date: Record<string, CrmActivity[]> }>(`/admin/crm/leads/${leadId}/activities`).then((r) => r.data);

export const postCrmLeadActivity = (
  leadId: number,
  payload: { body: string; kind?: string; visibility?: string },
) => api.post<CrmActivity>(`/admin/crm/leads/${leadId}/activities`, payload).then((r) => r.data);

export const checkCrmLeadDuplicate = (params: { phone?: string; telegram_id?: number }) =>
  api.get<{ matches: CrmLeadCard[] }>('/admin/crm/leads/check-duplicate', { params }).then((r) => r.data);

export const getCrmClientActivities = (userId: number) =>
  api.get<{ by_date: Record<string, CrmActivity[]> }>(`/admin/crm/clients/${userId}/activities`).then((r) => r.data);

export const exportCrmCsv = (params?: { manager_id?: number; days?: number }) =>
  api.get<string>('/admin/crm/export.csv', { params, responseType: 'text' }).then((r) => r.data);

export type LostReason = 'price' | 'timing' | 'competitor' | 'no_response' | 'other';

export const LOST_REASON_LABELS: Record<LostReason, string> = {
  price: 'Цена',
  timing: 'Сроки',
  competitor: 'Конкурент',
  no_response: 'Не отвечает',
  other: 'Другое',
};

export type CrmSearchResult = { type: 'order' | 'lead'; id: number; label: string; subtitle: string };

export const searchCrm = (q: string) =>
  api.get<{ results: CrmSearchResult[] }>('/admin/crm/search', { params: { q } }).then((r) => r.data);

export const createCrmLead = (payload: {
  title: string;
  phone?: string;
  telegram_id?: number;
  notes?: string;
  create_task_tomorrow?: boolean;
  assigned_manager_id?: number;
}) => api.post<CrmLeadCard>('/admin/crm/leads', payload).then((r) => r.data);

export const scheduleCrmLeadMeasurement = (
  leadId: number,
  payload: { city?: string; preferred_time?: string; notes?: string },
) =>
  api
    .post<{ ok: boolean; measurement_id: number; lead: CrmLeadCard }>(
      `/admin/crm/leads/${leadId}/measurement`,
      payload,
    )
    .then((r) => r.data);

export const convertCrmLead = (leadId: number) =>
  api
    .post<{
      ok: boolean;
      order_id: number;
      order_number: string;
      bitrix_deal_id?: string | null;
      bitrix_sync_error?: string | null;
    }>(`/admin/crm/leads/${leadId}/convert`)
    .then((r) => r.data);

export type CrmTask = {
  id: number;
  order_id?: number | null;
  lead_id?: number | null;
  assignee_id: number;
  created_by_id?: number | null;
  kind: string;
  title: string;
  notes?: string | null;
  due_at: string;
  status: string;
  completed_at?: string | null;
  created_at?: string | null;
  is_overdue: boolean;
};

export const getCrmOrderTasks = (orderId: number) =>
  api.get<CrmTask[]>(`/admin/crm/orders/${orderId}/tasks`).then((r) => r.data);

export const createCrmOrderTask = (
  orderId: number,
  payload: { kind?: string; title: string; notes?: string; due_at: string; assignee_id?: number },
) => api.post<CrmTask>(`/admin/crm/orders/${orderId}/tasks`, payload).then((r) => r.data);

export const patchCrmOrderTask = (
  orderId: number,
  taskId: number,
  payload: { status?: string; title?: string; notes?: string; due_at?: string },
) =>
  api.patch<CrmTask>(`/admin/crm/orders/${orderId}/tasks/${taskId}`, payload).then((r) => r.data);

export const patchCrmOrderSalesStage = (
  orderId: number,
  sales_stage: SalesStage,
  opts?: {
    force?: boolean;
    lost_reason?: LostReason;
    lost_note?: string;
    notify_client?: boolean;
    notifyClient?: boolean;
    client_message?: string;
    clientMessage?: string;
  },
) =>
  api
    .patch(`/admin/crm/orders/${orderId}/sales-stage`, {
      sales_stage,
      force: !!opts?.force,
      lost_reason: opts?.lost_reason,
      lost_note: opts?.lost_note,
      notify_client: opts?.notify_client ?? opts?.notifyClient ?? false,
      client_message: (opts?.client_message ?? opts?.clientMessage)?.trim() || undefined,
    })
    .then((r) => r.data);

export const ensureCrmOrderTask = (orderId: number) =>
  api.post<{ created: boolean; task?: CrmTask }>(`/admin/crm/orders/${orderId}/ensure-task`).then((r) => r.data);

export type CrmAlerts = {
  orders_without_valid_task: number;
  overdue_tasks: number;
  stuck_by_stage: Record<string, number>;
  stale_no_activity?: number;
  order_ids_without_task: number[];
  order_ids_stale?: number[];
};

export const getCrmAlerts = (params?: { manager_id?: number; include_unassigned?: boolean }) =>
  api.get<CrmAlerts>('/admin/crm/alerts', { params }).then((r) => r.data);

export type CrmMessage = {
  id: number;
  user_id: number;
  order_id?: number | null;
  direction: 'inbound' | 'outbound';
  channel: string;
  body?: string | null;
  payload?: Record<string, unknown>;
  sent_by_manager_id?: number | null;
  created_at?: string | null;
};

export type CrmClientContactPayload = {
  name?: string | null;
  phone?: string | null;
  company?: string | null;
  delivery_address?: string | null;
  sync_order_id?: number | null;
};

export type CrmClientContactResolved = {
  name: string;
  phone?: string | null;
  company?: string | null;
  telegram_id?: number | null;
  username?: string | null;
  delivery_address?: string | null;
  sync_order_id?: number | null;
};

export type CrmClient360 = {
  client_contact?: CrmClientContactResolved;
  primary_deal_id?: number | null;
  user: {
    id: number;
    telegram_id?: number | null;
    first_name?: string | null;
    last_name?: string | null;
    username?: string | null;
    phone?: string | null;
    role: string;
    assigned_manager_id?: number | null;
    crm_tags: string[];
    crm_department?: string | null;
  };
  orders: Array<{
    id: number;
    order_number: string;
    status: string | null;
    sales_stage: string | null;
    final_amount: number;
    assigned_manager_id?: number | null;
    created_at?: string | null;
  }>;
  measurements: Array<{ id: number; status: string | null; city?: string | null; created_at?: string | null }>;
  motor_quotes: Array<{ id: number; status: string; created_at?: string | null }>;
  recent_messages: CrmMessage[];
  timeline: Array<Record<string, unknown>>;
};

export const getCrmClient360 = (userId: number) =>
  api.get<CrmClient360>(`/admin/crm/clients/${userId}/360`).then((r) => r.data);

export type ClientDealSummaryItem = {
  id: number;
  order_number: string;
  sales_stage: string | null;
  item_count: number;
  delivery_address?: string | null;
  contact_name?: string | null;
};

export type ClientDealsSummary = {
  open_deals: ClientDealSummaryItem[];
  primary_deal_id: number | null;
  role: string | null;
};

export const getClientDealsSummary = (userId: number) =>
  api.get<ClientDealsSummary>(`/admin/crm/clients/${userId}/deals-summary`).then((r) => r.data);

export const ensureClientDeal = (
  userId: number,
  payload?: {
    city?: string;
    preferred_time?: string;
    notes?: string;
    schedule_measurement?: boolean;
  },
) =>
  api
    .post<{
      ok: boolean;
      order_id: number;
      order_number: string;
      created: boolean;
      measurement_id?: number | null;
      sales_stage?: string | null;
    }>(`/admin/crm/clients/${userId}/ensure-deal`, payload ?? {})
    .then((r) => r.data);

export const createClientCrmOrder = (
  userId: number,
  payload?: { notes?: string },
  opts?: { force_new?: boolean },
) =>
  api
    .post<{ ok: boolean; order_id: number; order_number: string; sales_stage?: string | null; created?: boolean }>(
      `/admin/crm/clients/${userId}/orders`,
      payload ?? {},
      { params: opts?.force_new ? { force_new: true } : {} },
    )
    .then((r) => r.data);

export const createClientCrmMeasurement = (
  userId: number,
  payload: { city?: string; preferred_time?: string; notes?: string },
) =>
  api
    .post<{
      ok: boolean;
      measurement_id: number;
      order_id?: number;
      order_number?: string;
      status?: string | null;
    }>(`/admin/crm/clients/${userId}/measurements`, payload)
    .then((r) => r.data);

export const getCrmClientMessages = (userId: number, params?: { before_id?: number; limit?: number }) =>
  api.get<CrmMessage[]>(`/admin/crm/clients/${userId}/messages`, { params }).then((r) => r.data);

export const sendCrmClientMessage = (userId: number, body: string, orderId?: number) =>
  api
    .post<CrmMessage>(`/admin/crm/clients/${userId}/messages`, { body, order_id: orderId })
    .then((r) => r.data);

export const patchCrmClientTags = (userId: number, tags: string[]) =>
  api.patch(`/admin/crm/clients/${userId}/tags`, { tags }).then((r) => r.data);

export const patchCrmClientDepartment = (userId: number, crm_department: string | null) =>
  api.patch(`/admin/crm/clients/${userId}/department`, { crm_department }).then((r) => r.data);

export const patchCrmClientContact = (userId: number, payload: CrmClientContactPayload) =>
  api
    .patch<{ ok: boolean; client_contact: CrmClientContactResolved; sync_order_id?: number | null }>(
      `/admin/crm/clients/${userId}/contact`,
      payload,
    )
    .then((r) => r.data);

export const patchCrmOrderContact = (orderId: number, payload: CrmClientContactPayload) =>
  api
    .patch<{ ok: boolean; client_contact: CrmClientContactResolved }>(
      `/admin/crm/orders/${orderId}/contact`,
      payload,
    )
    .then((r) => r.data);

export const patchCrmLead = (
  leadId: number,
  payload: { sales_stage?: SalesStage; notes?: string; title?: string; assigned_manager_id?: number | null },
) => api.patch<CrmLeadCard>(`/admin/crm/leads/${leadId}`, payload).then((r) => r.data);

export type V2FunnelInsights = {
  window_hours: number;
  totals: { v2_cart_items: number; v2_orders: number; cart_to_order_conversion_pct: number };
  last_window: { cart_to_order_conversion_pct: number };
  trend: { conversion_delta_pct: number; is_drop: boolean };
  by_category: Array<{ category: string; conversion_pct: number; is_drop: boolean }>;
};

export const getAdminV2FunnelInsights = (hours = 168) =>
  api.get<V2FunnelInsights>('/admin/insights/v2-funnel', { params: { hours } }).then((r) => r.data);

export const assignMeasurerToRequest = (measurementId: number, measurerUserId: number) =>
  api.patch(`/admin/measurements/${measurementId}/assign-measurer`, {
    measurer_user_id: measurerUserId,
  }).then((r) => r.data);

export const patchAdminMeasurementDetails = (
  measurementId: number,
  payload: { city?: string; preferred_time?: string; notes?: string },
) =>
  api
    .patch<{ ok: boolean; id: number; city?: string | null; preferred_time?: string | null; notes?: string | null }>(
      `/admin/measurements/${measurementId}/details`,
      payload,
    )
    .then((r) => r.data);

export const getAdminMeasurers = () => api.get('/admin/measurers').then((r) => r.data);

export const listMeasurementAttachments = (measurementId: number) =>
  api
    .get<{ items: CrmOrderBoardCard['attachments'] }>(`/admin/measurements/${measurementId}/attachments`)
    .then((r) => r.data.items);

export const uploadMeasurementAttachment = (measurementId: number, file: File, caption?: string) => {
  const form = new FormData();
  form.append('file', file);
  if (caption) form.append('caption', caption);
  return api
    .post<CrmOrderBoardCard['attachments'][0]>(`/admin/measurements/${measurementId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data);
};

export const patchMeasurementReport = (
  measurementId: number,
  payload: { report_notes?: string; mark_complete?: boolean; order_id?: number },
) =>
  api.patch(`/admin/measurements/${measurementId}/report`, payload).then((r) => r.data);

export const linkMeasurementToOrder = (measurementId: number, orderId: number) =>
  api
    .post(`/admin/measurements/${measurementId}/link-order/${orderId}`)
    .then((r) => r.data);

export const requestMotorWizardQuote = (payload: {
  width_m: number;
  height_m: number;
  estimated_load_kg: number;
  fabric_density: string;
  layers: string;
  preferred_power: string;
  preferred_protocol: string;
  top_recommendations: string[];
  project_name?: string;
  budget_rub?: number;
  deadline_note?: string;
}) => api.post('/measurements/motor-wizard-quote', payload).then((r) => r.data);

// ════════════════════════════════════════════════════════════════════════════
// PIM Admin API (Product Information Management)
// ════════════════════════════════════════════════════════════════════════════

export interface PIMMotor {
  id: number;
  tenant_id: string;
  slug: string;
  title: string;
  power_w?: number;
  noise_db?: number;
  load_kg?: number;
  ip_rating?: string;
  voltage?: string;
  is_active: boolean;
}

export interface PIMGlobalAttribute {
  id: number;
  attr_type: 'protocol' | 'power_type' | 'color';
  slug: string;
  title: string;
  icon?: string;
  is_global: boolean;
  is_active: boolean;
}

export interface PIMSystemMotorLink {
  id: number;
  motor_id: number;
  motor: PIMMotor;
  is_default: boolean;
  is_available: boolean;
  price_override: number | null;
  sort_order: number;
}

export interface PIMSystemAttributeLink {
  id: number;
  attribute_id: number;
  attribute: PIMGlobalAttribute;
}

export interface PIMSystemTechnical {
  motors: PIMSystemMotorLink[];
  protocols: PIMSystemAttributeLink[];
  power_types: PIMSystemAttributeLink[];
  colors: PIMSystemAttributeLink[];
}

export interface PIMMeasurementParams {
  min_width_cm?: number;
  max_width_cm?: number;
  min_drop_cm?: number;
  max_drop_cm?: number;
  side_clearance_cm?: number;
}

// Motors CRUD
export const getPIMMotors = (activeOnly = true): Promise<PIMMotor[]> =>
  api.get('/admin/pim/motors', { params: { active_only: activeOnly } }).then((r) => r.data);

export const createPIMMotor = (body: Omit<PIMMotor, 'id' | 'tenant_id' | 'is_active'>): Promise<PIMMotor> =>
  api.post('/admin/pim/motors', body).then((r) => r.data);

export const updatePIMMotor = (id: number, body: Partial<PIMMotor>): Promise<PIMMotor> =>
  api.put(`/admin/pim/motors/${id}`, body).then((r) => r.data);

export const deletePIMMotor = (id: number): Promise<{ ok: boolean; deleted: number }> =>
  api.delete(`/admin/pim/motors/${id}`).then((r) => r.data);

// Global Attributes (Protocols, Colors, Power Types)
export const getPIMAttributes = (attrType?: string): Promise<PIMGlobalAttribute[]> =>
  api.get('/admin/pim/attributes', { params: attrType ? { attr_type: attrType } : undefined }).then((r) => r.data);

// System Technical Management
export const getPIMSystemTechnical = (systemId: number): Promise<PIMSystemTechnical> =>
  api.get(`/admin/pim/systems/${systemId}/technical`).then((r) => r.data);

export const addPIMSystemMotor = (systemId: number, motorId: number, isDefault = false, sortOrder = 0): Promise<{ ok: boolean }> =>
  api.post(`/admin/pim/systems/${systemId}/motors/${motorId}`, null, { params: { is_default: isDefault, sort_order: sortOrder } }).then((r) => r.data);

export const removePIMSystemMotor = (systemId: number, motorId: number): Promise<{ ok: boolean }> =>
  api.delete(`/admin/pim/systems/${systemId}/motors/${motorId}`).then((r) => r.data);

export const addPIMSystemAttribute = (systemId: number, attributeId: number): Promise<{ ok: boolean }> =>
  api.post(`/admin/pim/systems/${systemId}/attributes/${attributeId}`).then((r) => r.data);

export const removePIMSystemAttribute = (systemId: number, attributeId: number): Promise<{ ok: boolean }> =>
  api.delete(`/admin/pim/systems/${systemId}/attributes/${attributeId}`).then((r) => r.data);

// Measurement Parameters
export const getPIMSystemMeasurement = (systemId: number): Promise<PIMMeasurementParams> =>
  api.get(`/admin/pim/systems/${systemId}/measurement`).then((r) => r.data);

export const updatePIMSystemMeasurement = (systemId: number, params: PIMMeasurementParams): Promise<{ ok: boolean }> =>
  api.put(`/admin/pim/systems/${systemId}/measurement`, params).then((r) => r.data);

// Patch motor link (is_available, is_default, price_override)
export const patchPIMMotorLink = (
  systemId: number,
  motorId: number,
  patch: { is_available?: boolean; is_default?: boolean; price_override?: number | null }
): Promise<{ ok: boolean }> =>
  api.patch(`/admin/pim/systems/${systemId}/motors/${motorId}`, patch).then((r) => r.data);

// Price tier CRUD (per-cell)
export const patchPriceTier = (systemId: number, tierId: number, data: Record<string, unknown>): Promise<Record<string, unknown>> =>
  api.patch(`/v3/admin/systems/${systemId}/price-tiers/${tierId}`, data).then((r) => r.data);

export const createPriceTier = (systemId: number, data: Record<string, unknown>): Promise<Record<string, unknown>> =>
  api.post(`/v3/admin/systems/${systemId}/price-tiers`, data).then((r) => r.data);

export const deletePriceTier = (systemId: number, tierId: number): Promise<{ ok: boolean }> =>
  api.delete(`/v3/admin/systems/${systemId}/price-tiers/${tierId}`).then((r) => r.data);

// Cache invalidation
export const invalidateCacheTag = (tag: string): Promise<{ ok: boolean; invalidated_tag: string }> =>
  api.post('/admin/cache/invalidate', null, { params: { tag } }).then((r) => r.data);

// Bulk price update
export const bulkPriceUpdate = (body: {
  system_ids: number[];
  mode: 'percent' | 'absolute';
  value: number;
}): Promise<{ ok: boolean; updated_tiers: number }> =>
  api.post('/admin/catalog/bulk-price-update', body).then((r) => r.data);

// Activity log
export interface ActivityLogEntry {
  id: number;
  table_name: string;
  record_id: number;
  action: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  user_id: number | null;
  created_at: string | null;
}

export const getActivityLog = (params?: {
  table_name?: string;
  record_id?: number;
  limit?: number;
}): Promise<ActivityLogEntry[]> =>
  api.get('/v3/admin/activity-log', { params }).then((r) => r.data);

// Admin catalog systems list (for bulk price modal)
export const getAdminSystemsList = (): Promise<AdminCatalogSystem[]> =>
  api.get('/admin/catalog-systems').then((r) => {
    const data = r.data;
    return Array.isArray(data) ? data : (data.items ?? data.systems ?? []);
  });

// Duplicate System
export const duplicatePIMSystem = (systemId: number): Promise<{ id: number; slug: string; title: string; message: string }> =>
  api.post(`/admin/pim/systems/${systemId}/duplicate`).then((r) => r.data);
